import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Card, CardBody, CardHeader, Modal, Row, Col, Alert, Spinner, Input, Button, Badge } from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../helpers/api';
import { spreadsheetRowNumber } from '../utils/importSheet';
import useImportJob from '../Hooks/useImportJob';
import ImportResumeBanner from '../components/Common/ImportResumeBanner';
import FeatherIcon from 'feather-icons-react';
import * as XLSX from 'xlsx';
import FileModal from '../components/Common/importExportModal';
import AssetModal from '../components/AssetModal';
import AssetTransferModal from '../components/AssetTransferModal';
import ParentAssetSelector from '../components/ParentAssetSelector';
import { formatCreatedDate, formatRoleName, formatDateForAPI, currencies, assetStatusOptions, BULK_TRANSFER_MAX_ASSETS } from '../constants/utils';
import AssetUsageStatusFields from '../components/AssetUsageStatusFields';
import TicketDetailsModal from '../components/TicketDetailsModal';
import LocationModal from '../components/LocationModal';
import ConfirmDeleteModal from '../components/Common/Modals/ConfirmDeleteModal';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getPresignedUrl, validateFiles, uploadDocument } from '../helpers/fileUpload';
import PermissionGuard from '../components/Common/PermissionGuard';
import usePermission from '../Hooks/usePermission';
import { useTranslation } from 'react-i18next';
import AssetShortcuts from '../components/AssetShortcuts';
import DynamicCustomFieldRenderer from '../components/DynamicCustomFieldRenderer';
import { ASSET_TRANSFER } from '../constants/permissions';

// Normalise an import date cell to DD-MM-YYYY regardless of source format.
// With XLSX raw:false, date serials arrive as formatted strings; this covers
// the common variants so users don't need to pre-format their files.
// Normalises an imported date cell to the backend's dd-MM-yyyy format (the
// @JsonFormat pattern on AssetRequest LocalDate fields). Anything it cannot
// confidently parse returns '' (blank) rather than the raw string, so a single
// malformed cell can never reject the whole import batch at the backend.
const normalizeImportDate = (value) => {
    if (!value && value !== 0) return '';

    // Excel serial number. Values at/below the epoch (<= 1, i.e. 30–31/12/1899)
    // are blank/zero cells Excel rendered as near-epoch dates — treat as empty.
    if (typeof value === 'number') {
        if (value <= 1) return '';
        const excelEpoch = new Date(1899, 11, 30);
        const date = new Date(excelEpoch.getTime() + value * 86400000);
        return [
            String(date.getDate()).padStart(2, '0'),
            String(date.getMonth() + 1).padStart(2, '0'),
            date.getFullYear(),
        ].join('-');
    }

    const str = value.toString().trim();
    if (!str) return '';
    if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str;                                          // dd-MM-yyyy (template/export)
    const iso = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (iso) return `${iso[3].padStart(2, '0')}-${iso[2].padStart(2, '0')}-${iso[1]}`;        // ISO yyyy-MM-dd

    // Slash- or dot-separated numeric dates (e.g. external exports). Interpreted
    // month-first (US M/D/Y) — the format of the source exports we ingest — with the
    // >12 rule overriding to day-first when unambiguous. Two-digit years expand to
    // 19xx/20xx; anything resolving before 1900 (Excel epoch) or out of range is blank.
    const parts = str.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{2,4})$/);
    if (parts) {
        const a = parseInt(parts[1], 10);
        const b = parseInt(parts[2], 10);
        let year = parseInt(parts[3], 10);
        if (parts[3].length <= 2) year = year <= 50 ? 2000 + year : 1900 + year;
        if (year < 1900) return '';                                                           // Excel epoch / garbage
        let month, day;
        if (a > 12 && b <= 12) { day = a; month = b; }                                        // clearly day-first
        else { month = a; day = b; }                                                          // month-first (US, default)
        if (month < 1 || month > 12 || day < 1 || day > 31) return '';
        return `${String(day).padStart(2, '0')}-${String(month).padStart(2, '0')}-${year}`;
    }

    return '';                                                                                // unrecognised → blank, never poison the batch
};

const Assets = () => {
    const { t , i18n} = useTranslation();
    document.title = `${t("shared.assets", "Assets")} | ArcMedix`;
    const [locations, setLocations] = useState([]);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [assets, setAssets] = useState([]);
    const validationTimeoutRef = useRef(null);
    const [modal_large, setmodal_large] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [errors, setErrors] = useState({});
    const [criticals, setCriticals] = useState([]);
    const [assetTypes, setAssetTypes] = useState([]);
    const [manufacturer, setManufacturer] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [apiError, setApiError] = useState('');
    const [currentImages, setCurrentImages] = useState([]);
    const [currentDocuments, setCurrentDocuments] = useState([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const [qrModal, setQrModal] = useState(false);
    const [enumData, setEnumData] = useState([]);
    const [assetNumberValidation, setAssetNumberValidation] = useState({
        isValidating: false,
        isValid: null,
        message: '',
        duplicateAssets: [],
        duplicateCount: 0
    });
    const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
    const [allowDuplicateAssetId, setAllowDuplicateAssetId] = useState(false);
    const [fileModal, setFileModal] = useState(false);
    // Bulk import runs as a server-side job: upload → preview → confirm. The hook owns the job id,
    // polls while work is in flight, and survives a page reload.
    const assetImport = useImportJob({
        entityType: 'ASSET',
        onCompleted: () => reloadAssets(),
    });
    const startImportJob = assetImport.start;
    const currentUser = JSON.parse(localStorage.getItem('authUser'));
    // Build a lightweight org object from the active context so this page
    // mirrors whichever org the user has currently switched into.
    const activeOrgId = currentUser?.activeOrganizationId ?? null;
    const activeOrgName = currentUser?.activeContextName
      || (currentUser?.memberships || []).find((m) => m.organizationId === activeOrgId)?.organizationName
      || null;
    const organization = activeOrgId ? { id: activeOrgId, name: activeOrgName } : null;
    const { isAdmin, isRequestor, hasPermission, visibilityRestricted } = usePermission();
    // Bulk transfer launched from the list toolbar reuses the shared Planning modal.
    const [showTransferModal, setShowTransferModal] = useState(false);
    // Row selection for bulk transfer. Keyed by asset id (not index) so ticks survive a Load More,
    // which refetches the whole list with a bigger limit rather than appending. Only a small
    // projection of each row is kept — enough to seed the transfer modal without a second fetch.
    const [selectedById, setSelectedById] = useState({});
    // True once "select all matching the filter" has selected the complete match set.
    const [allMatchingSelected, setAllMatchingSelected] = useState(false);
    const [selectingAllMatching, setSelectingAllMatching] = useState(false);
    // null | { type: 'cap' } | { type: 'cap_matching', total }
    const [selectionNotice, setSelectionNotice] = useState(null);
    // Post-transfer summary: { count, skipped, pending, scheduled }
    const [bulkResult, setBulkResult] = useState(null);

    // Check if user can delete an asset (must be owner or admin)
    const canDeleteAsset = (asset) => {
        if (isAdmin) return true;
        // createdBy can be an object with id/name or just an id
        const createdById = asset?.createdBy?.id ?? asset?.createdBy;
        const userId = currentUser?.id;
        // If createdBy not present (backend not updated), allow based on permission only
        if (createdById === undefined || createdById === null) return true;
        return createdById === userId;
    };
    const [qrGenerationSuccess, setQrGenerationSuccess] = useState(false);
    const [ticketModal, setTicketModal] = useState({
        isOpen: false,
        ticketData: null,
        loading: false
      });
    const [deleteConfirmation, setDeleteConfirmation] = useState({
        show: false,
        index: null,
        imageUrl: '',
        deleting: false
    });
    const [reportedByModal, setReportedByModal] = useState({
        isOpen: false,
        userData: null,
        loading: false,
        initialActiveTab: null
      });
      const [detailsModal, setDetailsModal] = useState({
          isOpen: false,
          type: null,
          title: '',
          mainData: null,
          relatedData: [],
          loading: false,
          locationData: null
        });
    
    const [count, setCount] = useState(10);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterModal, setFilterModal] = useState(false);
    const [activeShortcut, setActiveShortcut] = useState(null);
    // State for attach UUID modal
    const [attachUuidModal, setAttachUuidModal] = useState(false);
    const [attachUuidData, setAttachUuidData] = useState({ uuid: '', loading: false, error: '', searchTerm: '' });
    const [assetsWithoutUuid, setAssetsWithoutUuid] = useState([]);
    const [attachConfirmation, setAttachConfirmation] = useState({ show: false, asset: null });
    const [filters, setFilters] = useState({
        location: '',
        manufacturer: '',
        assetStatus: '',
        criticality: '',
        assetTypeId: '',
        vendorId: '',
        modelNumber: '',
        hasUuid: '',
        showDecommissioned: false
    });
    const [imageUploading, setImageUploading] = useState(false);
    const [docUploading, setDocUploading] = useState(false);
    // Org-defined custom fields (definitions are org-global; values are per-asset).
    const [customFieldDefs, setCustomFieldDefs] = useState([]);
    const [customFieldValues, setCustomFieldValues] = useState({});

    useEffect(() => {
        if (!activeOrgId) return;
        api.get(`custom-field/getAll?entityType=ASSET&organizationId=${activeOrgId}`)
            .then(({ data }) => setCustomFieldDefs(data?.response || []))
            .catch((err) => console.error('Failed to load custom field definitions:', err));
    }, [activeOrgId]);

    // Effective string value for a custom field, defaulting unticked checkboxes to "false".
    const customFieldValue = (def) => {
        const v = customFieldValues[def.id];
        if (def.fieldType === 'CHECKBOX') {
            return (v != null && String(v) !== '') ? String(v) : 'false';
        }
        if (def.fieldType === 'MULTISELECT') {
            // Value is a JSON array string; an empty selection counts as empty.
            try { const arr = JSON.parse(v || '[]'); return arr.length ? JSON.stringify(arr) : ''; }
            catch { return ''; }
        }
        return (v != null && String(v) !== '') ? String(v) : '';
    };

    // Parse one imported spreadsheet cell into the backend string form for a custom
    // field definition (mirrors customFieldValue's per-type encoding). Blank → ''.
    const parseCustomFieldCell = (def, raw) => {
        const s = raw == null ? '' : String(raw).trim();
        if (s === '') return '';
        switch (def.fieldType) {
            case 'CHECKBOX': {
                const low = s.toLowerCase();
                if (['false', 'no', 'non', '0', 'n'].includes(low)) return 'false';
                if (['true', 'yes', 'oui', '1', 'y', 'o'].includes(low)) return 'true';
                return 'false';
            }
            case 'MULTISELECT': {
                // Accept comma- or semicolon-separated options → JSON array (backend contract).
                const parts = s.split(/[;,]/).map(p => p.trim()).filter(Boolean);
                return parts.length ? JSON.stringify([...new Set(parts)]) : '';
            }
            case 'DATE': {
                const ddmmyyyy = normalizeImportDate(s); // '' or dd-MM-yyyy
                const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(ddmmyyyy);
                return m ? `${m[3]}-${m[2]}-${m[1]}` : '';         // backend LocalDate wants ISO yyyy-MM-dd
            }
            case 'DATETIME': {
                if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return s; // already an ISO datetime
                const dt = /^(\d{2})[-/](\d{2})[-/](\d{4})[ T](\d{2}):(\d{2})/.exec(s); // dd-MM-yyyy HH:mm
                if (dt) return `${dt[3]}-${dt[2]}-${dt[1]}T${dt[4]}:${dt[5]}:00`;
                const ddmmyyyy = normalizeImportDate(s);
                const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(ddmmyyyy);
                return m ? `${m[3]}-${m[2]}-${m[1]}T00:00:00` : '';    // coerce a bare date to midnight
            }
            default:
                return s; // TEXT, TEXTAREA, NUMBER, LINK, SELECT — pass through, backend validates
        }
    };

    const selectStyles = (hasError) => ({
        control: (base, state) => ({
            ...base,
            borderColor: hasError ? '#dc3545' : (state.isFocused ? '#86b7fe' : '#ced4da'),
            boxShadow: hasError
                ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
                : (state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none'),
            '&:hover': { borderColor: '#86b7fe' }
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#3980c0' : state.isFocused ? '#3980c0' : 'white',
            color: state.isSelected || state.isFocused ? 'white' : '#212529',
            '&:active': { backgroundColor: '#2e669a' }
        })
    });

    const multiSelectStyles = (hasError) => ({
        ...selectStyles(hasError),
        multiValue: (base) => ({
            ...base,
            backgroundColor: '#3980c0',
            borderRadius: '4px'
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: 'white',
            padding: '2px 6px'
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: 'white',
            '&:hover': {
                backgroundColor: '#2e669a',
                color: 'white'
            }
        })
    });

    const validateAssetNumber = async (assetNumber) => {
        if (!assetNumber || !assetNumber.trim()) {
            setAssetNumberValidation({
                isValidating: false,
                isValid: null,
                message: '',
                duplicateAsset: null
            });
            return;
        }

        // Don't validate if editing existing asset with same Asset ID
        if (selectedEntry?.id && selectedEntry?.assetNumber === assetNumber) {
            setAssetNumberValidation({
                isValidating: false,
                isValid: true,
                message: '',
                duplicateAsset: null
            });
            return;
        }

        setAssetNumberValidation({
            isValidating: true,
            isValid: null,
            message: '',
            duplicateAsset: null
        });

        try {
            const { data } = await api.get(`asset/validate-asset-number-with-details`, {
                params: {
                    assetNumber: assetNumber.trim(),
                    organizationId: organization?.id,
                    currentAssetId: selectedEntry?.id || null
                }
            });

            if (data.response.exists) {
                // Duplicate found - show warning with asset details
                setAssetNumberValidation({
                    isValidating: false,
                    isValid: false,
                    message: t("business.assets.asset_id_exists", 'This Asset ID already exists in your organization'),
                    duplicateAssets: data.response.assets || [],
                    duplicateCount: data.response.count || 0
                });
            } else {
                // Available
                setAssetNumberValidation({
                    isValidating: false,
                    isValid: true,
                    message: t("business.assets.asset_id_available", 'Asset ID is available'),
                    duplicateAssets: [],
                    duplicateCount: 0
                });
            }
        } catch (error) {
            console.error('Error validating Asset ID:', error);
            setAssetNumberValidation({
                isValidating: false,
                isValid: null,
                message: t("business.assets.error_validating_asset", 'Error validating Asset ID'),
                duplicateAssets: []
            });
        }
    };

    const handleAssetNumberChange = (value) => {
        setSelectedEntry({...selectedEntry, assetNumber: value});

        // Clear previous timeout
        if (validationTimeoutRef.current) {
            clearTimeout(validationTimeoutRef.current);
        }

        // Set new timeout for validation (debounce)
        validationTimeoutRef.current = setTimeout(() => {
            validateAssetNumber(value);
        }, 500);
    };

    const handleTicketClick = async (ticketNumber) => {
          setTicketModal(prev => ({
            ...prev,
            isOpen: true,
            ticketData: null,
            loading: true
          }));

          try {
            const { data } = await api.get(`asset-issues/organization/filtered`, {
              params: {
                ticket: ticketNumber
              }
            });

            setTicketModal(prev => ({
              ...prev,
              ticketData: data.response?.[0] || null,
              loading: false
            }));
          } catch (error) {
            console.error('Error fetching ticket details:', error);
            setTicketModal(prev => ({ ...prev, loading: false }));
          }
        };
       const fetchEnums = async () => {
          try {
            const { data } = await api.get("dynamic-enum/get-all");
            setEnumData(data.response || []);
          } catch (error) {
            console.error("Error fetching enums:", error);
          }
        };

    const fetchAssetTypes = async () => {
        try {
            const { data } = await api.get(`organization-asset-type/getAll?organizationId=${organization?.id}`);
            setAssetTypes(data.response || []);
        } catch (error) {
            console.error('Error fetching asset types:', error);
            setAssetTypes([]);
        }
    };

    const fetchLocations = async() => {
        const {data} = await api.get(`organization-location/get-by-organization-id?organizationId=${organization?.id}&count=9999&hierarchy=true`)
        setLocations(data.response[0]?.locations);
    };

    // Flatten the hierarchical asset tree returned by /asset/all?hierarchy=true into rows.
    // Each row keeps its `children` array so we know the count for the "N Sub-Assets" chip.
    const flattenAssetsWithCount = (tree, result = []) => {
        tree?.forEach((asset) => {
            result.push(asset);
            if (asset.children?.length > 0) {
                flattenAssetsWithCount(asset.children, result);
            }
        });
        return result;
    };

    // ── Bulk-transfer row selection ──────────────────────────────────────────────────────────
    // Keep only what the transfer modal needs, so selection state cannot go stale against a
    // refetched row and stays cheap when a few hundred assets are selected.
    const toSelectionRow = (asset) => ({
        id: asset.id,
        assetName: asset.assetName,
        assetNumber: asset.assetNumber,
        serialNumber: asset.serialNumber,
        currentLocation: asset.currentLocation,
        locationId: asset.locationId,
    });

    const canBulkTransfer = hasPermission(ASSET_TRANSFER) && !!organization?.id;
    const selectedCount = Object.keys(selectedById).length;
    const allLoadedSelected = assets.length > 0 && assets.every((a) => selectedById[a.id]);
    const someLoadedSelected = !allLoadedSelected && assets.some((a) => selectedById[a.id]);

    // Option objects in the exact shape AssetTransferModal's picker produces, so the modal can be
    // seeded without refetching. Memoized: the modal's open effect depends on this identity.
    const preselectedAssets = useMemo(() => Object.values(selectedById).map((a) => ({
        value: a.id,
        label: a.assetNumber ? `${a.assetName} (ID: ${a.assetNumber})` : a.assetName,
        chipLabel: a.assetNumber ? `${a.assetName} (${a.assetNumber})` : a.assetName,
        serialNumber: a.serialNumber,
        currentLocation: a.currentLocation,
        locationId: a.locationId,
    })), [selectedById]);

    const clearSelection = useCallback(() => {
        setSelectedById({});
        setAllMatchingSelected(false);
        setSelectionNotice(null);
    }, []);

    const toggleRow = (asset) => {
        setAllMatchingSelected(false);
        setSelectedById((prev) => {
            if (prev[asset.id]) {
                const next = { ...prev };
                delete next[asset.id];
                setSelectionNotice(null);
                return next;
            }
            if (Object.keys(prev).length >= BULK_TRANSFER_MAX_ASSETS) {
                setSelectionNotice({ type: 'cap' });
                return prev;
            }
            return { ...prev, [asset.id]: toSelectionRow(asset) };
        });
    };

    // Header checkbox: ticks/unticks the rows currently loaded. Selecting everything the filter
    // matches is the second, explicit step offered in the banner below the bar.
    const toggleSelectLoaded = () => {
        setAllMatchingSelected(false);
        if (allLoadedSelected) {
            clearSelection();
            return;
        }
        setSelectedById((prev) => {
            const next = { ...prev };
            let truncated = false;
            assets.forEach((a) => {
                if (next[a.id]) return;
                if (Object.keys(next).length >= BULK_TRANSFER_MAX_ASSETS) {
                    truncated = true;
                    return;
                }
                next[a.id] = toSelectionRow(a);
            });
            setSelectionNotice(truncated ? { type: 'cap' } : null);
            return next;
        });
    };

    // Re-runs the current filter with no limit (count: 0) to select every matching asset. There is
    // no total-count endpoint, so the count can only be reported after this fetch.
    const selectAllMatching = async () => {
        setSelectingAllMatching(true);
        try {
            const { data } = await api.get('asset/all', { params: buildListParams({ count: 0 }) });
            // Unfiltered responses are a tree; filtered ones are already flat. Flatten either way.
            const rows = flattenAssetsWithCount(data?.response?.[0]?.assets || []);
            const capped = rows.slice(0, BULK_TRANSFER_MAX_ASSETS);
            setSelectedById(Object.fromEntries(capped.map((r) => [r.id, toSelectionRow(r)])));
            if (rows.length > BULK_TRANSFER_MAX_ASSETS) {
                setSelectionNotice({ type: 'cap_matching', total: rows.length });
                setAllMatchingSelected(false);
            } else {
                setSelectionNotice(null);
                setAllMatchingSelected(true);
            }
        } catch (error) {
            console.error('Error selecting all matching assets:', error);
        } finally {
            setSelectingAllMatching(false);
        }
    };

    const flattenLocationsWithPath = (locs, parentPath = '') => {
        const result = [];
        locs?.forEach(loc => {
            const path = parentPath ? `${parentPath} → ${loc.locationName}` : loc.locationName;
            result.push({ id: loc.id, name: loc.locationName, path });
            if (loc.subLocations?.length > 0) {
                result.push(...flattenLocationsWithPath(loc.subLocations, path));
            }
        });
        return result;
    };

    const flatLocations = useMemo(() => flattenLocationsWithPath(locations), [locations]);

     const fetchManufacturer = async() => {
        const {data} = await api.get(`asset/get-manufacturer`)
        setManufacturer(data.response?.manufacturers);
    };

    const fetchVendors = async() => {
        try {
            const {data} = await api.get(`vendor/getAllVendors`);
            setVendors(data.response || []);
        } catch (error) {
            console.error('Error fetching vendors:', error);
            setVendors([]);
        }
    };

    const handleSearch = useCallback((e) => {
        const term = e.target.value;
        setSearchTerm(term);
        setAssets([]);
        setCount(10);
        setHasMore(true);
    }, []);

    const handleDetailsClick = async (type, data, contextData = null) => {
        // Only the location flow is reachable here today — asset/org rows route through AssetModal.
        if (type !== 'location') return;

        let locationData = null;
        if (typeof data === 'string') {
          const locationName = data;
          const foundLocation = locations?.find(loc => {
            if (loc.locationName === locationName) return true;
            return loc.subLocations?.some(sub => sub.locationName === locationName);
          });
          if (foundLocation) {
            if (foundLocation.locationName === locationName) {
              locationData = { ...foundLocation, organization: organization };
            } else {
              const sub = foundLocation.subLocations.find(s => s.locationName === locationName);
              locationData = { ...sub, organization: organization };
            }
          }
        } else if (typeof data === 'object') {
          locationData = { ...data, organization: organization };
        }

        const mainName = typeof data === 'string' ? data : (data?.locationName || data);

        setDetailsModal(prev => ({
          ...prev,
          isOpen: true,
          type: 'location',
          title: t("shared.location_details", 'Location Details'),
          mainData: mainName,
          relatedData: [],
          loading: true,
          contextData,
          locationData,
        }));

        try {
          const { data: response } = await api.get('asset-issues/organization/filtered', {
            params: { organizationLocation: mainName },
          });
          setDetailsModal(prev => ({ ...prev, relatedData: response.response || [], loading: false }));
        } catch (error) {
          console.error('Error fetching location details:', error);
          setDetailsModal(prev => ({ ...prev, loading: false }));
        }
      };

    const buildShortcutParams = (key) => {
        switch (key) {
            case 'critical_assets':
                return { criticality: 'High' };
            case 'critical_no_preventive':
                return { criticality: 'High', withoutPreventiveWo: true };
            case 'critical_offline':
                return { criticality: 'High', assetStatus: 'OFFLINE' };
            case 'open_reactive_wo':
                return { hasOpenReactiveWo: true };
            case 'never_maintained':
                return { neverMaintained: true };
            case 'offline':
                return { assetStatus: 'OFFLINE' };
            default:
                return {};
        }
    };

    const handleShortcutClick = (key) => {
        // Toggling a shortcut clears any modal filters so the two don't fight.
        if (key) {
            setFilters({
                location: '',
                manufacturer: '',
                assetStatus: '',
                criticality: '',
                assetTypeId: '',
                vendorId: '',
                modelNumber: '',
                hasUuid: ''
            });
        }
        setActiveShortcut(key);
        setAssets([]);
        setCount(10);
        setHasMore(true);
    };

    // An admin can change this user's team scope while the page is mounted; the rows already on

    // screen are then stale (they may include assets the user can no longer open).

    useEffect(() => {

        const refetchOnScopeChange = () => { setAssets([]); setCount(10); setHasMore(true); };

        window.addEventListener('visibility-scope-changed', refetchOnScopeChange);

        return () => window.removeEventListener('visibility-scope-changed', refetchOnScopeChange);

    }, []);

    /**
     * Builds the `asset/all` query for the current search + filters + shortcut. Extracted so
     * "select all matching the filter" can re-issue the EXACT same query with `count: 0`
     * (no limit) instead of duplicating — and drifting from — the filter logic.
     */
    const buildListParams = useCallback((overrides = {}) => {
        const params = {
            organizationId:organization?.id,
            count,
            search: searchTerm,
            manufacturer: filters.manufacturer
        };
        // filters.location holds a location id. The backend expands it to its subtree, so picking
        // a parent still returns everything beneath it.
        if (filters.location) params.locationIds = filters.location;
        if (filters.assetStatus) params.assetStatus = filters.assetStatus;
        if (filters.criticality) params.criticality = filters.criticality;
        if (filters.assetTypeId) params.assetTypeId = filters.assetTypeId;
        if (filters.vendorId) params.vendorId = filters.vendorId;
        if (filters.modelNumber) params.modelNumber = filters.modelNumber;
        if (filters.hasUuid !== '' && filters.hasUuid !== undefined && filters.hasUuid !== null) {
            params.hasUuid = filters.hasUuid;
        }
        // Decommissioned assets are hidden by default. The toggle in the filter panel,
        // or an explicit status filter of DECOMMISSIONED, brings them back in.
        if (filters.showDecommissioned || filters.assetStatus === 'DECOMMISSIONED') {
            params.includeDecommissioned = true;
        }

        // Shortcut params override matching modal filters (shortcut wins).
        Object.assign(params, buildShortcutParams(activeShortcut));

        return { ...params, ...overrides };
    }, [count, searchTerm, filters, activeShortcut, organization?.id]);

    // A commit keeps running server-side even if the tab is closed, and a preview waits 24h for a
    // decision. On mount, pick either back up instead of pretending the import never happened.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const resumable = await assetImport.findResumable();
            if (!cancelled && resumable?.id) {
                await assetImport.attach(resumable.id);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organization?.id]);

    /**
     * Filter parameters for a full export: the same filters the list is showing, but unpaginated
     * and flat. Exporting only the rows that happen to be loaded would hand the user a file that
     * silently drops most of their register — and re-importing it would look like it had worked.
     *
     * @returns {object} query params for `asset/all`
     */
    const buildExportParams = () => buildListParams({ count: 100000, hierarchy: false });

    const fetchAssetFromOrganization = useCallback(async () => {

        setLoading(true);
        try {
            const {data} = await api.get(`asset/all`, { params: buildListParams() });
            // A filter that matches nothing comes back as `response: []`, so there is no [0] to read.
            // Indexing it unguarded threw and landed in the catch below, which logged a misleading
            // "Error fetching assets" and skipped the hasMore reset — an empty result is not an error.
            const tree = data?.response?.[0]?.assets || [];
            setAssets(flattenAssetsWithCount(tree));
            setHasMore(tree.length >= count);
        } catch (error) {
            console.error('Error fetching assets:', error);
        } finally {
            setLoading(false);
        }
    }, [buildListParams, count]);

    const handleLoadMore = () => {
        setCount(prevCount => prevCount + 10);
    };

    const handleClose = () => {
        setFileModal(false);
        setApiError('');
        // A finished import must not linger in state: reopening the dialog would show the previous
        // run's result screen instead of letting the user upload the next file.
        if (assetImport.job && !['VALIDATING', 'READY', 'COMMITTING'].includes(assetImport.job.status)) {
            assetImport.reset();
        }
    };

    // Complete field mapping for import/export
    // Single source of truth for the asset import template AND the parser.
    // `template` powers handleDownloadTemplate: keeping headers, descriptions,
    // format hints, and example rows in one entry per column makes drift impossible.
    const tplDesc = (k) => t(`business.assets.import.template.descriptions.${k}`);
    const tplFmt = (k) => t(`business.assets.import.template.formats.${k}`);
    const tplHeader = (k) => t(`business.assets.import.template.headers.${k}`, k);
    const ASSET_FIELD_MAPPING = {
        // The column that makes editing an export possible. Blank creates a new asset; a value
        // updates the asset it came from — the only way a rename can work, since the name is the
        // very thing being changed. Distinct from 'Asset ID', which is the customer's own asset
        // number and is not unique.
        'Arcmedix ID': {
            field: 'arcmedixId', type: 'string',
            template: { headerI18nKey: 'arcmedixId', headerLabel: tplHeader('arcmedixId'), description: tplDesc('arcmedixId'), formatHint: tplFmt('arcmedixId'), example1: '', example2: '' },
        },
        'Name': {
            field: 'assetName', required: true, type: 'string',
            template: { headerI18nKey: 'name', headerLabel: tplHeader('name'), description: tplDesc('name'), formatHint: tplFmt('name'), example1: 'Rooftop HVAC Unit 01', example2: 'Backup Generator 01' },
        },
        'Parent Arcmedix ID': {
            field: 'parentArcmedixId', type: 'string',
            template: { headerI18nKey: 'parentArcmedixId', headerLabel: tplHeader('parentArcmedixId'), description: tplDesc('parentArcmedixId'), formatHint: tplFmt('parentArcmedixId'), example1: '', example2: '' },
        },
        'Parent Asset': {
            field: 'parentAsset', type: 'resolve_parent',
            template: { headerI18nKey: 'parentAsset', headerLabel: tplHeader('parentAsset'), description: tplDesc('parentAsset'), formatHint: tplFmt('parentAsset'), example1: '', example2: 'Rooftop HVAC Unit 01' },
        },
        'Asset ID': {
            field: 'assetNumber', type: 'string',
            template: { headerI18nKey: 'assetId', headerLabel: tplHeader('assetId'), description: tplDesc('assetNumber'), formatHint: tplFmt('assetNumber'), example1: 'AST-HVAC-001', example2: 'AST-GEN-001' },
        },
        'Serial No.': {
            field: 'serialNumber', type: 'string', aliases: ['Serial Number'],
            template: { headerI18nKey: 'serialNo', headerLabel: tplHeader('serialNo'), description: tplDesc('serialNumber'), formatHint: tplFmt('serialNumber'), example1: 'HV-23001', example2: 'GN-67890' },
        },
        'Manufacturer': {
            field: 'manufacturer', type: 'string',
            template: { headerI18nKey: 'manufacturer', headerLabel: tplHeader('manufacturer'), description: tplDesc('manufacturer'), formatHint: tplFmt('manufacturer'), example1: 'Carrier', example2: 'Generac' },
        },
        'Model Number': {
            field: 'modelNumber', type: 'string',
            template: { headerI18nKey: 'modelNumber', headerLabel: tplHeader('modelNumber'), description: tplDesc('modelNumber'), formatHint: tplFmt('modelNumber'), example1: '48TC-A12', example2: 'RG022' },
        },
        'Asset Type': {
            field: 'assetType', type: 'resolve_asset_type',
            template: { headerI18nKey: 'assetType', headerLabel: tplHeader('assetType'), description: tplDesc('assetType'), formatHint: tplFmt('assetType'), example1: 'HVAC', example2: 'Power' },
        },
        'Location': {
            field: 'location', type: 'string',
            template: { headerI18nKey: 'location', headerLabel: tplHeader('location'), description: tplDesc('location'), formatHint: tplFmt('location'), example1: 'Building A', example2: 'Building B' },
        },
        'Sub Location': {
            field: 'subLocation', type: 'string',
            template: { headerI18nKey: 'subLocation', headerLabel: tplHeader('subLocation'), description: tplDesc('subLocation'), formatHint: tplFmt('subLocation'), example1: 'Roof', example2: 'Mechanical Room' },
        },
        'Asset Status': {
            field: 'assetStatus', type: 'string', aliases: ['Status'],
            template: { headerI18nKey: 'assetStatus', headerLabel: tplHeader('assetStatus'), description: tplDesc('assetStatus'), formatHint: tplFmt('assetStatus'), example1: t('shared.online', 'Online'), example2: t('shared.offline', 'Offline') },
        },
        'Criticality': {
            field: 'criticality', type: 'string',
            template: { headerI18nKey: 'criticality', headerLabel: tplHeader('criticality'), description: tplDesc('criticality'), formatHint: tplFmt('criticality'), example1: t('shared.high', 'High'), example2: t('shared.medium', 'Medium') },
        },
        'Vendor Name': {
            field: 'vendorName', type: 'resolve_vendor',
            template: { headerI18nKey: 'vendorName', headerLabel: tplHeader('vendorName'), description: tplDesc('vendorName'), formatHint: tplFmt('vendorName'), example1: 'Carrier Direct', example2: 'PowerLease Inc' },
        },
        'Description': {
            field: 'description', type: 'string',
            template: { headerI18nKey: 'description', headerLabel: tplHeader('description'), description: tplDesc('description'), formatHint: tplFmt('description'), example1: 'Rooftop air conditioning unit serving Floor 3', example2: 'Standby diesel generator for emergency backup' },
        },
        'Asset Cost': {
            field: 'assetCost', type: 'number',
            template: { headerI18nKey: 'assetCost', headerLabel: tplHeader('assetCost'), description: tplDesc('assetCost'), formatHint: tplFmt('assetCost'), example1: '18500', example2: '12000' },
        },
        'Year of Purchase': {
            field: 'yearOfPurchase', type: 'integer',
            template: { headerI18nKey: 'yearOfPurchase', headerLabel: tplHeader('yearOfPurchase'), description: tplDesc('yearOfPurchase'), formatHint: tplFmt('yearOfPurchase'), example1: '2023', example2: '2022' },
        },
        'Commissioning Date': {
            field: 'commissioningDate', type: 'date',
            template: { headerI18nKey: 'commissioningDate', headerLabel: tplHeader('commissioningDate'), description: tplDesc('commissioningDate'), formatHint: tplFmt('commissioningDate'), example1: '15-01-2023', example2: '10-06-2022' },
        },
        'Estimated Lifespan': {
            field: 'estimatedLifespan', type: 'integer',
            template: { headerI18nKey: 'estimatedLifespan', headerLabel: tplHeader('estimatedLifespan'), description: tplDesc('estimatedLifespan'), formatHint: tplFmt('estimatedLifespan'), example1: '15', example2: '20' },
        },
        'Acquisition Type': {
            field: 'acquisitionType', type: 'string',
            template: { headerI18nKey: 'acquisitionType', headerLabel: tplHeader('acquisitionType'), description: tplDesc('acquisitionType'), formatHint: tplFmt('acquisitionType'), example1: t('business.assets.purchased', 'Purchased'), example2: t('business.assets.rental', 'Rental') },
        },
        'Acquisition Start Date': {
            field: 'acquisitionStartDate', type: 'date',
            template: { headerI18nKey: 'acquisitionStartDate', headerLabel: tplHeader('acquisitionStartDate'), description: tplDesc('acquisitionStartDate'), formatHint: tplFmt('acquisitionStartDate'), example1: '', example2: '10-06-2022' },
        },
        'Currency': {
            field: 'currency', type: 'string',
            template: { headerI18nKey: 'currency', headerLabel: tplHeader('currency'), description: tplDesc('currency'), formatHint: tplFmt('currency'), example1: 'USD', example2: 'USD' },
        },
        'NFC/RFID Tag UUID': {
            field: 'uuid', type: 'string',
            template: { headerI18nKey: 'uuid', headerLabel: tplHeader('uuid'), description: tplDesc('uuid'), formatHint: tplFmt('uuid'), example1: '', example2: '' },
        },
        'Acquisition End Date': {
            field: 'acquisitionEndDate', type: 'date',
            template: { headerI18nKey: 'acquisitionEndDate', headerLabel: tplHeader('acquisitionEndDate'), description: tplDesc('acquisitionEndDate'), formatHint: tplFmt('acquisitionEndDate'), example1: '', example2: '10-06-2025' },
        },
    };

    // Get all known column headers (for detecting unmatched columns)
    // Normalise a header cell for matching: fold non-breaking spaces and curly
    // apostrophes, collapse whitespace, trim and lowercase — so "Nom ", "Nom ",
    // "Type d’actif" and "Type d'actif" all resolve to the same key.
    const normalizeHeader = (h) => String(h ?? '')
        .replace(/ /g, ' ')
        .replace(/[‘’]/g, "'")
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

    // Issues come back keyed by canonical field name; the user only ever saw the column header, so
    // map back to it before showing them anything.
    const importColumnLabels = Object.entries(ASSET_FIELD_MAPPING).reduce((acc, [header, cfg]) => {
        acc[cfg.field] = cfg.template?.headerLabel || header;
        return acc;
    }, {});

    const getKnownHeaders = () => {
        const headers = new Set();
        Object.keys(ASSET_FIELD_MAPPING).forEach(header => {
            headers.add(normalizeHeader(header));
            const mapping = ASSET_FIELD_MAPPING[header];
            if (mapping.aliases) {
                mapping.aliases.forEach(alias => headers.add(normalizeHeader(alias)));
            }
            if (mapping.template?.headerLabel) {
                headers.add(normalizeHeader(mapping.template.headerLabel));
            }
        });
        // Custom-field columns are known headers too, so their labels never leak into
        // the unmatched-columns `meta` bucket.
        customFieldDefs.forEach(def => headers.add(normalizeHeader(def.label)));
        headers.add('#'); // Exclude row number column
        return headers;
    };

    const handleImport = async (file) => {
        try {
            setLoading(true);
            setApiError('');

            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { raw: true });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            const knownHeaders = getKnownHeaders();

            // Cross-locale header lookup: maps any supported locale's column
            // header translation to its ASSET_FIELD_MAPPING key, so a French
            // template can be imported when the app is set to English and vice versa.
            const crossLocaleHeaderMap = {};
            const loadedLangs = Object.keys(i18n.store?.data || {});
            Object.keys(ASSET_FIELD_MAPPING).forEach(mapKey => {
                crossLocaleHeaderMap[normalizeHeader(mapKey)] = mapKey;
                ASSET_FIELD_MAPPING[mapKey].aliases?.forEach(a => {
                    crossLocaleHeaderMap[normalizeHeader(a)] = mapKey;
                });
                const i18nSuffix = ASSET_FIELD_MAPPING[mapKey].template?.headerI18nKey;
                if (i18nSuffix) {
                    loadedLangs.forEach(lang => {
                        const bundle = i18n.getResourceBundle(lang, 'translation');
                        const label = bundle?.business?.assets?.import?.template?.headers?.[i18nSuffix];
                        if (label) crossLocaleHeaderMap[normalizeHeader(label)] = mapKey;
                    });
                }
            });

            // Detect the header row instead of assuming a fixed offset. The template
            // ships 2 instruction rows above the headers, but users routinely delete
            // them (headers then sit on row 1) or paste their data into an own sheet.
            // Scan the first rows as raw arrays and pick the one that actually holds
            // recognised column headers — it must include the required Name/Nom column.
            const sheetRange = worksheet['!ref'] ? XLSX.utils.decode_range(worksheet['!ref']) : null;
            const originRow = sheetRange ? sheetRange.s.r : 0;
            const rawRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false });

            let headerOffset = -1;
            let bestHeaderScore = 0;
            const scanLimit = Math.min(rawRows.length, 15);
            for (let i = 0; i < scanLimit; i++) {
                const cells = rawRows[i] || [];
                let score = 0;
                let hasName = false;
                cells.forEach(cell => {
                    const key = crossLocaleHeaderMap[normalizeHeader(cell)];
                    if (key) {
                        score += 1;
                        if (key === 'Name') hasName = true;
                    }
                });
                // Require Name plus at least one more known header so a data row that
                // happens to match a single column can't be mistaken for the header.
                if (hasName && score >= 2 && score > bestHeaderScore) {
                    bestHeaderScore = score;
                    headerOffset = i;
                }
            }

            if (headerOffset === -1) {
                setApiError(t("business.assets.import.header_not_found",
                    'Could not find the column header row. Make sure your sheet has a header row that includes a "Name" column.'));
                return;
            }

            // rawRows[i] maps to absolute sheet row (originRow + i); read using that row as the header.
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false, range: originRow + headerOffset });

            if (jsonData.length === 0) {
                setApiError(t("business.assets.import.no_data", 'No data found in the file'));
                return;
            }

            // Reverse lookup: translated label → backend enum value, built across
            // EVERY loaded locale (not just the active one) so a French-filled file
            // (e.g. "Acheté", "Disponible") converts correctly in an English session
            // and vice versa — same cross-locale principle as the header matcher.
            const reverseEnumMap = {};
            const acquisitionTypeKeys = [
                { value: 'PURCHASED', key: 'business.assets.purchased', fallback: 'Purchased' },
                { value: 'RENTAL',    key: 'business.assets.rental',    fallback: 'Rental' },
                { value: 'DEMO',      key: 'business.assets.demo',      fallback: 'Demo' },
                { value: 'PLACEMENT', key: 'business.assets.placement', fallback: 'Placement' },
                { value: 'DONATION',  key: 'business.assets.donation',  fallback: 'Donation/Grant' },
            ];
            loadedLangs.forEach(lang => {
                const lt = i18n.getFixedT(lang);
                assetStatusOptions(lt).forEach(o => {
                    if (o.label) reverseEnumMap[o.label.toLowerCase()] = o.value;
                });
                acquisitionTypeKeys.forEach(o => {
                    reverseEnumMap[lt(o.key, o.fallback).toLowerCase()] = o.value;
                });
                criticals.forEach(c => {
                    reverseEnumMap[lt(`shared.${c.toLowerCase()}`, c).toLowerCase()] = c;
                });
            });

            // Custom-field columns match by their (org-defined) label, keyed the same
            // normalised way as native headers so a CF named e.g. "Risk Class" resolves
            // regardless of spacing/case.
            const cfByLabel = {};
            customFieldDefs.forEach(def => { cfByLabel[normalizeHeader(def.label)] = def; });

            // --- Parse rows into raw asset objects ---
            const rows = jsonData
                .map((item, rowIndex) => {
                    // The organization comes from the caller's session server-side; sending it from
                    // the browser would just be a claim the server has to ignore anyway.
                    const asset = {
                        __rowNumber: spreadsheetRowNumber(rowIndex, originRow, headerOffset),
                    };
                    const unmatchedData = {};
                    const cfCells = {}; // definitionId -> parsed backend value for this row

                    Object.keys(item).forEach(header => {
                        const value = item[header];
                        if (value === undefined || value === null || value === '') return;

                        const mappingKey = crossLocaleHeaderMap[normalizeHeader(header)];

                        if (mappingKey) {
                            const mapping = ASSET_FIELD_MAPPING[mappingKey];

                            switch (mapping.type) {
                                case 'string':
                                    asset[mapping.field] = value?.toString() || '';
                                    break;
                                case 'number':
                                    asset[mapping.field] = parseFloat(value) || 0;
                                    break;
                                case 'integer':
                                    asset[mapping.field] = parseInt(value) || null;
                                    break;
                                case 'date':
                                    asset[mapping.field] = normalizeImportDate(value);
                                    break;
                                case 'resolve_parent':
                                case 'resolve_asset_type':
                                case 'resolve_vendor':
                                    // Store raw value for resolution later
                                    asset[mapping.field] = value?.toString().trim() || '';
                                    break;
                                default:
                                    asset[mapping.field] = value?.toString() || '';
                            }

                            if ((asset[mapping.field] === '' || asset[mapping.field] === null) && mapping.default) {
                                asset[mapping.field] = mapping.default;
                            }
                        } else {
                            const cfDef = cfByLabel[normalizeHeader(header)];
                            if (cfDef) {
                                const parsed = parseCustomFieldCell(cfDef, value);
                                if (parsed !== '') cfCells[cfDef.id] = parsed;
                            } else if (!knownHeaders.has(normalizeHeader(header))) {
                                const strValue = String(value).trim();
                                if (strValue !== '' && strValue !== 'undefined' && strValue !== 'null') {
                                    unmatchedData[header] = strValue;
                                }
                            }
                        }
                    });

                    // Only default the currency on rows that create something. On an update row a
                    // defaulted value is indistinguishable from one the user typed, and would
                    // rewrite the currency of every asset in the file.
                    if (!asset.currency && !asset.arcmedixId) {
                        asset.currency = 'USD';
                    }

                    if (Object.keys(unmatchedData).length > 0) {
                        asset.meta = JSON.stringify(unmatchedData);
                    }

                    // Attach custom-field values for every active definition (matching the
                    // create form, which submits all defs). An absent CF column defaults to
                    // '' — or 'false' for a checkbox — so the backend's required-field check
                    // (CF-6) still fires on any missing required field.
                    // Keyed by label, not definition id: the server matches the sheet's column
                    // headings, and only values the file actually carried are sent — a column the
                    // user deleted must leave the stored value alone.
                    if (Object.keys(cfCells).length > 0) {
                        const byLabel = {};
                        customFieldDefs.forEach(def => {
                            const v = cfCells[def.id];
                            if (v != null && v !== '') byLabel[def.label] = v;
                        });
                        if (Object.keys(byLabel).length > 0) asset.__customFields = byLabel;
                    }

                    // Normalise enum fields: accept translated labels (e.g. "Disponible") or raw
                    // English enum values (e.g. "ONLINE") — reverse map covers both.
                    if (asset.assetStatus) {
                        const raw = asset.assetStatus.trim();
                        asset.assetStatus = reverseEnumMap[raw.toLowerCase()]
                            || raw.toUpperCase().replace(/\s+/g, '_');
                    }
                    // Decommissioned assets require a reason; the import has no reason
                    // column, so default it (the backend rejects DECOMMISSIONED with no
                    // reason). The backend defaults the decommission date to today.
                    if (asset.assetStatus === 'DECOMMISSIONED' && !asset.decommissionedReason) {
                        asset.decommissionedReason = t('business.assets.import.default_decommission_reason', 'Unplanned');
                    }
                    if (asset.acquisitionType) {
                        const raw = asset.acquisitionType.trim();
                        asset.acquisitionType = reverseEnumMap[raw.toLowerCase()]
                            || raw.toUpperCase().replace(/\s+/g, '_');
                    }
                    if (asset.criticality) {
                        const raw = asset.criticality.trim();
                        asset.criticality = reverseEnumMap[raw.toLowerCase()] || raw;
                    }

                    return asset;
                })
                // Drop rows that carried nothing at all (trailing blank lines in the sheet).
                .filter(asset => Object.keys(asset)
                    .some(key => key !== '__rowNumber' && key !== '__customFields'));

            if (rows.length === 0) {
                setApiError(t("business.assets.import.no_data", 'No data found in the file'));
                return;
            }

            // Hand the parsed rows to the server and stop. Validation, the preview and the write
            // all happen there now: the browser used to pre-create vendors and asset types, resolve
            // parents, and then POST every row in one long request — which left orphaned lookup rows
            // behind whenever the import failed, and duplicated assets whenever the response was
            // lost. The user sees what the file will do before anything is written.
            const created = await startImportJob({
                fileName: file.name,
                rows: rows.map(({ __rowNumber, __customFields, ...values }) => ({
                    rowNumber: __rowNumber,
                    values,
                    customFields: __customFields || {},
                })),
            });
            if (created) {
                setFileModal(true);
            }
            return { success: true, count: rows.length };
        } catch (error) {
            const errorMsg = typeof error.message === 'object'
                ? JSON.stringify(error.message)
                : (error.message || t("shared.error_processing_file", "Error processing the file"));
            setApiError(errorMsg);
            throw new Error(errorMsg);
        } finally {
            setLoading(false);
        }
    };
    
    const handleExport = async () => {
        const exportDate = new Date().toISOString().split('T')[0];

        // The list is paginated; the export must not be. Re-fetch everything the current filters
        // match so the file is the register, not the visible slice of it.
        let assetsToExport = assets;
        try {
            setLoading(true);
            const { data } = await api.get('asset/all', { params: buildExportParams() });
            const tree = data?.response?.[0]?.assets || [];
            // De-duplicate by id. An asset that has children is returned both in its own right and
            // again inside its parent's subAssets, so flattening alone emits it twice — and the
            // importer rejects a file whose Arcmedix ID repeats, which would block the very
            // export → edit → re-import round trip this file exists for.
            const seenIds = new Set();
            assetsToExport = flattenAssetsWithCount(tree).filter((a) => {
                if (a?.id == null) return true;
                if (seenIds.has(a.id)) return false;
                seenIds.add(a.id);
                return true;
            });
        } catch (e) {
            console.warn('Falling back to the loaded page for export:', e?.message);
        } finally {
            setLoading(false);
        }

        if (!assetsToExport || assetsToExport.length === 0) return;

        // Helper to resolve location/sub-location from hierarchy
        const resolveLocationHierarchy = (asset) => {
            const locName = asset?.currentLocation;
            if (!locName) return { location: '', subLocation: '' };

            // Check if it's a sub-location
            const locLower = locName.toLowerCase();
            for (const loc of (locations || [])) {
                const sub = loc.subLocations?.find(s => s.locationName?.toLowerCase() === locLower);
                if (sub) {
                    return { location: loc.locationName, subLocation: sub.locationName };
                }
                if (loc.locationName?.toLowerCase() === locLower) {
                    return { location: loc.locationName, subLocation: '' };
                }
            }
            return { location: locName, subLocation: '' };
        };

        // Custom-field columns, rendered back into their import form (comma-joined
        // multiselect, DD-MM-YYYY dates) so an export round-trips through handleImport.
        const exportCustomFieldValue = (def, cf) => {
            const v = cf?.value;
            if (v == null || v === '') return '';
            switch (def.fieldType) {
                case 'MULTISELECT': try { return JSON.parse(v).join(', '); } catch { return v; }
                case 'CHECKBOX': return v === 'true' ? 'true' : 'false';
                case 'DATE': { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v); return m ? `${m[3]}-${m[2]}-${m[1]}` : v; }
                case 'DATETIME': { const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(v); return m ? `${m[3]}-${m[2]}-${m[1]} ${m[4]}:${m[5]}` : v; }
                default: return v;
            }
        };

        const dataToExport = assetsToExport.map((asset) => {
            const { location, subLocation } = resolveLocationHierarchy(asset);
            const cfColumns = {};
            (customFieldDefs || []).forEach((def) => {
                const cf = (asset?.customFields || []).find(c => c.definitionId === def.id);
                cfColumns[def.label] = exportCustomFieldValue(def, cf);
            });
            return {
                // Template-matching columns (re-importable). The id comes first so it is the first
                // thing a user sees when they open the file, and hard to delete by accident.
                'Arcmedix ID': asset?.id ?? '',
                'Name': asset?.assetName || '',
                'Parent Arcmedix ID': asset?.parentId ?? asset?.parent?.id ?? '',
                'Parent Asset': asset?.parent?.assetName || '',
                'Asset ID': asset?.assetNumber || '',
                'Serial No.': asset?.serialNumber || '',
                'Manufacturer': asset?.manufacturer || '',
                'Model Number': asset?.modelNumber || '',
                'Asset Type': asset?.assetTypes?.map(at => at.name).join(', ') || '',
                'Location': location,
                'Sub Location': subLocation,
                'Asset Status': asset?.assetStatus || '',
                'Criticality': asset?.criticality || '',
                'Vendor Name': asset?.vendorName || '',
                'Description': asset?.description || '',
                'Asset Cost': asset?.assetCost || '',
                'Currency': asset?.currency || 'USD',
                'Year of Purchase': asset?.yearOfPurchase || '',
                'Commissioning Date': asset?.commissioningDate || '',
                'Estimated Lifespan': asset?.estimatedLifespan || '',
                'Acquisition Type': asset?.acquisitionType || '',
                'Acquisition Start Date': asset?.acquisitionStartDate || '',
                'Acquisition End Date': asset?.acquisitionEndDate || '',
                'NFC/RFID Tag UUID': asset?.uuid || '',
                'Warranty Start Date': asset?.warrantyMaintenanceRequest?.manufacturerWarrantyStartDate || '',
                'Warranty End Date': asset?.warrantyMaintenanceRequest?.manufacturerWarrantyEndDate || '',
                'Extension Start Date': asset?.warrantyMaintenanceRequest?.warrantyExtensionStartDate || '',
                'Extension End Date': asset?.warrantyMaintenanceRequest?.warrantyExtensionEndDate || '',
                // Custom-field columns (re-importable), placed before the export-only computed ones
                ...cfColumns,
                // Export-only computed columns
                'Annual Depreciation': asset?.annualDepreciation || '',
                'Accumulated Depreciation': asset?.accumulatedDepreciation || '',
                'Net Book Value': asset?.netBookValue || '',
                'QR Payload': asset?.qrPayload || asset?.qrImageUrl || '',
            };
        });

        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Assets');

        XLSX.writeFile(wb, `assets_${exportDate}.xlsx`);
    };

    const handleDownloadTemplate = async () => {
        // ExcelJS is dynamically imported so its ~1MB footprint only loads
        // when a user actually clicks "Download Template". SheetJS still
        // owns the import (read) path; we only swapped the writer because
        // SheetJS community edition can't emit cell comments or dropdowns.
        const ExcelJS = (await import('exceljs')).default;

        // Every row is built by iterating ASSET_FIELD_MAPPING in declaration
        // order, so descriptions, format hints, and example rows can never
        // drift out of sync with headers — that drift was the original bug.
        const columns = Object.entries(ASSET_FIELD_MAPPING);
        const pick = (key) => columns.map(([, cfg]) => cfg.template?.[key] ?? '');

        // Active custom-field definitions become extra trailing template columns so a
        // downloaded template exposes every CF the org expects (label + guidance +
        // example), and a filled template round-trips through handleImport (CF-15).
        const cfDefs = customFieldDefs || [];
        const cfFormatHint = (def) => {
            const opts = (def.options || []).join(', ');
            switch (def.fieldType) {
                case 'SELECT': return t('business.assets.import.template.cf.select', 'One of: {{options}}', { options: opts });
                case 'MULTISELECT': return t('business.assets.import.template.cf.multiselect', 'Comma-separated; any of: {{options}}', { options: opts });
                case 'CHECKBOX': return t('business.assets.import.template.cf.checkbox', 'true or false');
                case 'NUMBER': return t('business.assets.import.template.cf.number', 'Number');
                case 'DATE': return t('business.assets.import.template.cf.date', 'Date (DD-MM-YYYY)');
                case 'DATETIME': return t('business.assets.import.template.cf.datetime', 'Date & time (DD-MM-YYYY HH:mm)');
                case 'LINK': return t('business.assets.import.template.cf.link', 'URL (https://…) or tel: number');
                default: return t('business.assets.import.template.cf.text', 'Text');
            }
        };
        const cfExample = (def) => {
            switch (def.fieldType) {
                case 'SELECT': return (def.options && def.options[0]) || '';
                case 'MULTISELECT': return (def.options || []).slice(0, 2).join(', ');
                case 'CHECKBOX': return 'true';
                case 'NUMBER': return '10';
                case 'DATE': return '15-01-2023';
                case 'DATETIME': return '15-01-2023 09:30';
                case 'LINK': return 'https://example.com';
                default: return '';
            }
        };
        const cfDescription = (def) => {
            const base = def.helpText || def.label;
            return def.required
                ? `${base} ${t('business.assets.import.template.cf.required_suffix', '(required)')}`
                : base;
        };

        const headerRow = [...columns.map(([header, cfg]) => cfg.template?.headerLabel ?? header), ...cfDefs.map(d => d.label)];
        const descriptions = [...pick('description'), ...cfDefs.map(cfDescription)];
        const formatHints = [...pick('formatHint'), ...cfDefs.map(cfFormatHint)];
        const example1 = [...pick('example1'), ...cfDefs.map(cfExample)];
        const example2 = [...pick('example2'), ...cfDefs.map(cfExample)];

        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Asset Import Template');

        // handleImport auto-detects the header row, so the exact offset here is no
        // longer load-bearing, but keep the order — descriptions, formatHints,
        // headers, examples — so a freshly downloaded template reads cleanly.
        ws.addRows([descriptions, formatHints, headerRow, example1, example2]);

        // Light-blue fill on the two instruction rows.
        const lightBlue = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFD6EAF8' },
        };
        [1, 2].forEach((rowIdx) => {
            const row = ws.getRow(rowIdx);
            for (let c = 1; c <= headerRow.length; c++) {
                row.getCell(c).fill = lightBlue;
            }
        });

        // Cell comments on the header row (row 3). The same description +
        // format hint that occupy rows 1 and 2 are anchored to row 3 so the
        // guidance survives if a user deletes the instruction rows.
        const headerExcelRow = ws.getRow(3);
        headerExcelRow.font = { bold: true };
        columns.forEach(([, cfg], idx) => {
            const tpl = cfg.template;
            if (!tpl) return;
            headerExcelRow.getCell(idx + 1).note = {
                texts: [{ text: `${tpl.description}\n\n${tpl.formatHint}` }],
                margins: { insetmode: 'auto' },
            };
        });
        // Same guidance for the trailing custom-field columns.
        cfDefs.forEach((def, idx) => {
            headerExcelRow.getCell(columns.length + idx + 1).note = {
                texts: [{ text: `${cfDescription(def)}\n\n${cfFormatHint(def)}` }],
                margins: { insetmode: 'auto' },
            };
        });

        // Data-validation dropdowns on the columns whose values are enum-like.
        // Excel's inline list source is comma-delimited regardless of locale,
        // so this works in both en and fr Excel. Range covers rows 4-10000 so
        // dropdowns appear on every row a user might reasonably add.
        const enumLists = {
            [t('business.assets.import.template.headers.assetStatus', 'Asset Status')]:
                assetStatusOptions(t)
                    .filter(o => ['ONLINE', 'OFFLINE', 'DO_NOT_TRACK', 'DECOMMISSIONED'].includes(o.value))
                    .map(o => o.label),
            [t('business.assets.import.template.headers.acquisitionType', 'Acquisition Type')]: [
                t('business.assets.purchased', 'Purchased'),
                t('business.assets.rental',    'Rental'),
                t('business.assets.demo',      'Demo'),
                t('business.assets.placement', 'Placement'),
                t('business.assets.donation',  'Donation/Grant'),
            ],
        };
        if (criticals && criticals.length > 0) {
            enumLists[t('business.assets.import.template.headers.criticality', 'Criticality')] =
                criticals.map(c => t(`shared.${c.toLowerCase()}`, c));
        }
        // Single-select custom fields get a dropdown of their options too.
        cfDefs.filter(d => d.fieldType === 'SELECT' && (d.options || []).length > 0)
            .forEach(d => { enumLists[d.label] = d.options; });
        // Excel column letter for a 0-based index — spreads past Z (AA, AB, …) so
        // dropdowns still land on the right column once CF columns push past 26.
        const colLetter = (idx0) => {
            let s = '', n = idx0;
            do { s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26) - 1; } while (n >= 0);
            return s;
        };
        Object.entries(enumLists).forEach(([header, values]) => {
            const idx = headerRow.indexOf(header);
            if (idx < 0) return;
            const letter = colLetter(idx);
            ws.dataValidations.add(`${letter}4:${letter}10000`, {
                type: 'list',
                allowBlank: true,
                formulae: [`"${values.join(',')}"`],
                showErrorMessage: true,
                errorStyle: 'warning',
                error: t('business.assets.import.template.invalid_value', 'Value not in the allowed list'),
            });
        });

        // Column widths.
        headerRow.forEach((h, idx) => {
            ws.getColumn(idx + 1).width = Math.max(h.length + 4, 20);
        });

        // Trigger browser download.
        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'asset_import_template.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const reloadAssets = useCallback(() => {
        setAssets([]);
        setCount(10);
        setHasMore(true);
        // Whatever caused the reload (transfer, delete, tag attach) may have removed or moved the
        // selected rows, so the ticks no longer describe what is on screen.
        clearSelection();
        fetchAssetFromOrganization();
    }, [fetchAssetFromOrganization, clearSelection]);

    /**
     * Summarises a completed bulk transfer for the banner, then refreshes the list.
     * `scheduled` comes from the modal (a future start date schedules rather than moves);
     * `skippedAssetIds` are assets the backend dropped because they already stood at the
     * destination.
     */
    const handleBulkTransferSuccess = useCallback((response, meta) => {
        setBulkResult({
            count: response?.quantity ?? 0,
            skipped: response?.skippedAssetIds?.length || 0,
            pending: response?.status === 'PENDING',
            scheduled: !!meta?.scheduled,
        });
        reloadAssets();
    }, [reloadAssets]);

    const fetchCriticality = async () => {
        try {
            const { data } = await api.get("dynamic-enum/find-by-name?name=criticality");
            setCriticals(data.response.values)   
        } catch (error) {
            console.error("Error fetching enums:", error);
            return null;
        }
    };

    const openQrModal = (entry) => {
        setSelectedEntry(entry);
        setQrModal(true);
    };

    const getAssetStatusBadgeClass = (status) => {
        if (!status) return 'bg-secondary';

        const statusUpper = status.toUpperCase();

        switch (statusUpper) {
            case 'ONLINE':
            case 'IN_USE':
            case 'STANDBY':
                return 'bg-success';
            case 'OFFLINE':
            case 'OFFLINE_PLANNED':
            case 'PLANNED_DOWNTIME':
            case 'OFFLINE_UNPLANNED':
            case 'UNPLANNED_DOWNTIME':
                return 'bg-danger';
            case 'DO_NOT_TRACK':
            case 'AVAILABLE':
            case 'UNAVAILABLE':
            case 'NONE':
                return 'bg-secondary';
            case 'DECOMMISSIONED':
            case 'DISPOSED':
                return 'bg-dark';
            default:
                return 'bg-secondary';
        }
    };

    const downloadQRCode = async (imageUrl, assetName, serialNumber) => {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `QR_${assetName || 'Asset'}_${serialNumber || 'ID'}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Download failed:', error);
            setApiError(t("business.assets.qr.download_failed", 'Failed to download QR code'));
        }
    };

    const handleQr = async() => {
        try {
            setApiError('');
            const {data} = await api.get('get-qr');
            
            const updatedEntry = {
                ...selectedEntry,
                qrPayload: data.response.uuid,
                qrImageUrl: data.response.imageUrl
            };
            
            setSelectedEntry(updatedEntry);
            setQrGenerationSuccess(true);
            
            setTimeout(() => setQrGenerationSuccess(false), 3000);
            
            if (selectedEntry?.id) {
                await api.put(`asset/update?assetId=${selectedEntry.id}`, updatedEntry);
                await fetchAssetFromOrganization();
            }
        } catch (error) {
            console.error('QR generation failed:', error);
            setApiError(t("business.assets.qr.generation_failed", 'Failed to generate QR code'));
        }
    }

    const deleteImageFromAsset = async (updatedImages) => {
        try {
            if (selectedEntry?.id) {
                await api.put(`asset/update?assetId=${selectedEntry.id}`, {
                    ...selectedEntry,
                    assetImageUrls: updatedImages,
                    currentLocation: selectedEntry?.locationId
                });
                await fetchAssetFromOrganization();
            }
            return true;
        } catch (error) {
            console.error("Failed to delete image:", error);
            setApiError(error?.response?.data?.error || t("business.assets.delete_image_failed", "Failed to delete image"));
            return false;
        }
    };

    const handleFileChange = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        // Validate files before upload
        const validation = validateFiles(Array.from(files));
        if (validation.errors.length > 0) {
            setApiError(validation.errors.join('\n'));
        }

        if (validation.validFiles.length === 0) {
            e.target.value = '';
            return;
        }

        setImageUploading(true);
        try {
            const uploadedUrls = [];

            for (let i = 0; i < validation.validFiles.length; i++) {
                const file = validation.validFiles[i];
                const publicURL = await getPresignedUrl(file);
                if (publicURL) {
                    uploadedUrls.push(publicURL);
                }
            }

            setSelectedEntry(prev => ({
                ...prev,
                assetImageUrls: [...(prev.assetImageUrls || []), ...uploadedUrls]
            }));
            // Also update currentImages for consistency
            setCurrentImages(prev => [...prev, ...uploadedUrls]);

            // Reset file input
            e.target.value = '';
        } catch (err) {
            setApiError(t("business.assets.upload_error", "Error during upload: {{message}}", { message: err.message }));
        } finally {
            setImageUploading(false);
        }
    };

    const handleDocumentChange = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        // Validate files before upload
        const validation = validateFiles(Array.from(files));
        if (validation.errors.length > 0) {
            setApiError(validation.errors.join('\n'));
        }

        if (validation.validFiles.length === 0) {
            e.target.value = '';
            return;
        }

        setDocUploading(true);
        try {
            const uploadedUrls = [];

            for (let i = 0; i < validation.validFiles.length; i++) {
                const file = validation.validFiles[i];
                const publicURL = await uploadDocument(file);
                if (publicURL) {
                    uploadedUrls.push(publicURL);
                }
            }

            setSelectedEntry(prev => ({
                ...prev,
                documentUrls: [...(prev.documentUrls || []), ...uploadedUrls]
            }));
            // Also update currentDocuments for consistency
            setCurrentDocuments(prev => [...prev, ...uploadedUrls]);

            // Reset file input
            e.target.value = '';
        } catch (err) {
            setApiError(t("business.assets.upload_error", "Error during upload: {{message}}", { message: err.message }));
        } finally {
            setDocUploading(false);
        }
    };

    const handleDeleteDocument = (index) => {
        const updatedDocuments = selectedEntry?.documentUrls?.filter((_, i) => i !== index) || [];
        setSelectedEntry(prev => ({
            ...prev,
            documentUrls: updatedDocuments
        }));
        setCurrentDocuments(updatedDocuments);
    };

    const getDocumentIcon = (url) => {
        const ext = url?.split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'pdf':
                return 'file-text';
            case 'doc':
            case 'docx':
                return 'file';
            case 'xls':
            case 'xlsx':
                return 'grid';
            default:
                return 'file';
        }
    };

    const getFileNameFromUrl = (url) => {
        try {
            const pathname = new URL(url).pathname;
            return decodeURIComponent(pathname.split('/').pop() || 'Document');
        } catch {
            return url?.split('/').pop() || 'Document';
        }
    };


    const tog_large = (entry = null, initialParent = null) => {
        setmodal_large(!modal_large);

        // Reset validation state
        setAssetNumberValidation({
            isValidating: false,
            isValid: null,
            message: ''
        });

        // Clear any pending validation timeout
        if (validationTimeoutRef.current) {
            clearTimeout(validationTimeoutRef.current);
        }

        // Build the custom-field values map from an asset response's customFields array.
        const buildCfValues = (src) => (src?.customFields || []).reduce((acc, c) => {
            if (c && c.definitionId != null) acc[c.definitionId] = c.value ?? '';
            return acc;
        }, {});

        if (entry) {
            // Initialize currentImages and currentDocuments for edit mode
            setCurrentImages(entry.assetImageUrls || []);
            setCurrentDocuments(entry.documentUrls || []);

            const buildEntryState = (src) => {
                const locationId = src.locationId || '';
                const vendorId = vendors.find(v => v.vendorName === src.vendorName)?.id || '';
                let deviceClassificationsArray = [];
                if (src.deviceClassifications) {
                    if (Array.isArray(src.deviceClassifications)) {
                        deviceClassificationsArray = src.deviceClassifications;
                    } else if (typeof src.deviceClassifications === 'string') {
                        deviceClassificationsArray = src.deviceClassifications.includes(',')
                            ? src.deviceClassifications.split(',').map(item => item.trim())
                            : [src.deviceClassifications];
                    }
                }
                // Backend serializes decommissionedOn as dd-MM-yyyy; the form uses ISO yyyy-MM-dd
                // (HTML5 date input format). Normalize on load so the field round-trips correctly.
                const decommissionedOnIso = (() => {
                    const v = src.decommissionedOn;
                    if (!v) return '';
                    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
                    const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(v);
                    return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
                })();
                return {
                    ...src,
                    locationId,
                    vendorId,
                    deviceClassifications: deviceClassificationsArray,
                    decommissionedOn: decommissionedOnIso,
                    // Snapshot the persisted status at open time so the revive-only-admin guard
                    // can compare the current edit value against the original DB value.
                    _persistedAssetStatus: src.assetStatus || null,
                };
            };

            // Open immediately with whatever data we have so the modal isn't blocked on a fetch.
            setSelectedEntry(buildEntryState(entry));
            setCustomFieldValues(buildCfValues(entry));

            // The flat list response omits the nested `parent` object. Fetch the full record so
            // the Parent Asset selector pre-fills correctly on edit.
            if (entry.id && !entry.parent) {
                api.get(`asset/get-by-id?id=${entry.id}`)
                    .then(({ data }) => {
                        const full = data?.response;
                        if (full) {
                            setSelectedEntry((prev) => prev?.id === full.id ? buildEntryState(full) : prev);
                            setCustomFieldValues(buildCfValues(full));
                        }
                    })
                    .catch((err) => console.error('Failed to fetch full asset for edit:', err));
            }
        } else {
            // Clear images and documents for new asset
            setCurrentImages([]);
            setCurrentDocuments([]);
            setSelectedEntry({
                assetName: '',
                serialNumber: '',
                manufacturer: '',
                assetTypeIds: [], // Asset types multi-select
                currentLocation: initialParent?.currentLocation || '',
                locationId: initialParent?.locationId || '',
                vendorId: '',
                averagePricePerCase: '',
                averageDurationPerCaseHours: '',
                acquisitionType: 'PURCHASED',
                acquisitionStartDate: '',
                acquisitionEndDate: '',
                assetCost: '',
                currency: 'USD',
                commissioningDate: '',
                estimatedLifespan: '',
                acceleratedDepreciation: false,
                depreciationPeriodYears: '',
                assetImageUrls: [],
                documentUrls: [],
                uuid: '',
                parent: initialParent
            });
            // Seed defaults for create (definitions are org-global; loaded on mount).
            const seeded = {};
            customFieldDefs.forEach((d) => {
                if (d.defaultValue != null && d.defaultValue !== '') seeded[d.id] = d.defaultValue;
            });
            setCustomFieldValues(seeded);
        }

        setErrors({});
        setApiError('');
    };

    const handleModalClose = () => {
        setmodal_large(false);
        setSelectedEntry(null);
        setErrors({});
        setAssetNumberValidation({
            isValidating: false,
            isValid: null,
            message: '',
            duplicateAsset: null
        });
        setAllowDuplicateAssetId(false);
        setShowDuplicateConfirm(false);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!selectedEntry?.assetName?.trim()) newErrors.assetName = t("business.assets.errors.asset_name_required", 'Asset name is required');
        if (!selectedEntry?.locationId) newErrors.currentLocation = t("shared.location_required", 'Location is required');
        if (!selectedEntry?.assetStatus?.trim()) newErrors.assetStatus = t("business.assets.errors.asset_status_required", 'Asset Status is required');
        if (selectedEntry?.assetStatus === 'DECOMMISSIONED' && !selectedEntry?.decommissionedReason?.trim()) {
            newErrors.decommissionedReason = t('business.assets.errors.decommission_reason_required', 'Decommission reason is required');
        }

        // Required custom fields
        customFieldDefs.forEach((def) => {
            if (def.required && customFieldValue(def) === '') {
                newErrors[`cf_${def.id}`] = t('custom_fields.value_required', '{{label}} is required', { label: def.label });
            }
        });

        // Accelerated depreciation validation
        if (selectedEntry?.acceleratedDepreciation) {
            if (!selectedEntry?.depreciationPeriodYears) {
                newErrors.depreciationPeriodYears = t("business.assets.errors.depreciation_period_required", 'Depreciation period is required when accelerated depreciation is enabled');
            } else if (parseInt(selectedEntry.depreciationPeriodYears) <= 0) {
                newErrors.depreciationPeriodYears = t("business.assets.errors.depreciation_period_gt_zero", 'Depreciation period must be greater than 0');
            }
        }

        // Check for duplicate Asset ID (only if not already confirmed)
        if (assetNumberValidation.isValid === false && !allowDuplicateAssetId) {
            // Show confirmation dialog instead of blocking
            setShowDuplicateConfirm(true);
            return;
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            setApiError('');

            // Prepare payload with deviceClassifications as array
            // Convert empty strings to null for numeric and unique fields
            const payload = {
                ...selectedEntry,
                organizationId: organization?.id,
                currentLocation: selectedEntry?.locationId,
                deviceClassifications: Array.isArray(selectedEntry?.deviceClassifications)
                    ? selectedEntry.deviceClassifications
                    : [],
                depreciationPeriodYears: selectedEntry?.depreciationPeriodYears ? parseInt(selectedEntry.depreciationPeriodYears) : null,
                acceleratedDepreciation: selectedEntry?.acceleratedDepreciation === true,
                estimatedLifespan: selectedEntry?.estimatedLifespan ? parseInt(selectedEntry.estimatedLifespan) : null,
                replacementThresholdPercent: selectedEntry?.replacementThresholdPercent === '' || selectedEntry?.replacementThresholdPercent == null
                    ? null
                    : parseFloat(selectedEntry.replacementThresholdPercent),
                assetCost: selectedEntry?.assetCost ? parseFloat(selectedEntry.assetCost) : null,
                averagePricePerCase: selectedEntry?.averagePricePerCase ? parseFloat(selectedEntry.averagePricePerCase) : null,
                averageDurationPerCaseHours: selectedEntry?.averageDurationPerCaseHours ? parseFloat(selectedEntry.averageDurationPerCaseHours) : null,
                yearOfPurchase: selectedEntry?.yearOfPurchase ? parseInt(selectedEntry.yearOfPurchase) : null,
                vendorId: selectedEntry?.vendorId ? parseInt(selectedEntry.vendorId) : null,
                locationId: selectedEntry?.locationId ? parseInt(selectedEntry.locationId) : null,
                // Convert empty strings to null for unique/optional string fields
                uuid: selectedEntry?.uuid || null,
                gmdnCode: selectedEntry?.gmdnCode || null,
                cnehCode: selectedEntry?.cnehCode || null,
                qrPayload: selectedEntry?.qrPayload || null,
                acquisitionType: selectedEntry?.acquisitionType || null,
                acquisitionStartDate: selectedEntry?.acquisitionStartDate || null,
                acquisitionEndDate: selectedEntry?.acquisitionEndDate || null,
                decommissionedOn: formatDateForAPI(selectedEntry?.decommissionedOn) || null,
                parentId: selectedEntry?.parent?.id ?? null,
                customFieldValues: customFieldDefs.map((def) => ({
                    definitionId: def.id,
                    value: customFieldValue(def),
                })),
            };
            delete payload.parent;
            delete payload.customFields; // response-only field; not part of AssetRequest

            if (selectedEntry?.id) {
                await api.put(`asset/update?assetId=${selectedEntry?.id}`, payload);
            } else {
                await api.post('asset/create-by-admin', [payload]);
            }

            await reloadAssets()
            handleModalClose();
            setAllowDuplicateAssetId(false); // Reset for next time
        } catch (error) {
            setApiError(error?.response?.data?.message || t("shared.something_went_wrong", "Something went wrong!"));
        }
    };

      const handleReportedByClick = async (userId, fromDate = null, toDate = null, initialActiveTab = null) => {
        if (!userId) return;

        setReportedByModal(prev => ({
          ...prev,
          isOpen: true,
          userData: null,
          loading: true,
          initialActiveTab,
        }));
    
        try {
          const { data: userData } = await api.get(`asset/get-by-id?id=${userId}`);
    
          const params = {
            assetId: userId,
            organizationId:organization?.id
          };
    
          if (fromDate) params.fromDate = fromDate;
          if (toDate) params.toDate = toDate;
    
          const { data: issuesData } = await api.get(`asset-issues/organization/filtered`, { params });
    
          setReportedByModal(prev => ({
            ...prev,
            userData: {
              ...userData.response,
              reportedIssues: issuesData.response || []
            },
            loading: false
          }));
        } catch (error) {
          console.error('Error fetching reported by details:', error);
          setReportedByModal(prev => ({ ...prev, loading: false }));
        }
      };

    const handleDelete = async () => {
        try {
            await api.delete(`asset?assetId=${selectedEntry?.id}`);
            await reloadAssets()
            setDeleteModal(false);
        } catch (error) {
            console.error('Error deleting asset:', error);
            setApiError(t("business.assets.delete_failed", 'Failed to delete asset'));
        }
    }

    const clearFilters = () => {
        setFilters({
            location: '',
            manufacturer: '',
            assetStatus: '',
            criticality: '',
            assetTypeId: '',
            vendorId: '',
            modelNumber: '',
            hasUuid: ''
        });
        setFilterModal(false);
    };


    useEffect(() => {
        fetchCriticality()
        fetchManufacturer()
        fetchEnums()
        fetchVendors()
        fetchAssetTypes()
    }, []);

     const onStatusChange = async (data, value,id) => {
          try {
            const payload = { id: data?.id, status: value };
            // Set resolvedAt timestamp when marking as RESOLVED
            if (value === 'RESOLVED') {
              payload.resolvedAt = new Date().toISOString();
            }
            await api.put('asset-issues/update', payload);
            handleReportedByClick(id)
          } catch (error) {
            console.error('Failed to update status:', error);
          }
        };

    useEffect(() => {
        fetchLocations()
    },[])

    useEffect(() => {
        fetchAssetFromOrganization();
    }, [fetchAssetFromOrganization]);

    // Drop the selection whenever the result set the user was choosing from changes, so a ticked
    // asset can never be transferred from a filter the user has since moved away from. `count` is
    // deliberately NOT a dependency: Load More widens the same result set, and selection is keyed
    // by id, so ticks survive it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { clearSelection(); }, [searchTerm, filters, activeShortcut, organization?.id]);

    // Cleanup validation timeout on unmount
    useEffect(() => {
        return () => {
            if (validationTimeoutRef.current) {
                clearTimeout(validationTimeoutRef.current);
            }
        };
    }, []);

    // Handle createWithUuid query parameter from deep links
    useEffect(() => {
        const createWithUuid = searchParams.get('createWithUuid');
        if (createWithUuid) {
            // Open modal with UUID pre-filled
            setCurrentImages([]);
            setSelectedEntry({
                assetName: '',
                serialNumber: '',
                manufacturer: '',
                assetTypeIds: [], // Asset types multi-select
                currentLocation: '',
                locationId: '',
                vendorId: '',
                averagePricePerCase: '',
                averageDurationPerCaseHours: '',
                acquisitionType: 'PURCHASED',
                acquisitionStartDate: '',
                acquisitionEndDate: '',
                assetCost: '',
                currency: 'USD',
                commissioningDate: '',
                estimatedLifespan: '',
                acceleratedDepreciation: false,
                depreciationPeriodYears: '',
                assetImageUrls: [],
                uuid: createWithUuid // Pre-fill UUID from deep link
            });
            setErrors({});
            setApiError('');
            setmodal_large(true);
            // Clear the query parameter
            setSearchParams({});
        }
    }, [searchParams, setSearchParams]);

    // Handle attachUuid query parameter from deep links
    useEffect(() => {
        const attachUuid = searchParams.get('attachUuid');
        if (attachUuid) {
            setAttachUuidData({ uuid: attachUuid, loading: true, error: '', searchTerm: '' });
            setAttachUuidModal(true);
            // Fetch assets without UUID
            fetchAssetsWithoutUuid();
            // Clear the query parameter
            setSearchParams({});
        }
    }, [searchParams, setSearchParams]);

    // Deep link: open the create form pre-filled with a parent asset
    useEffect(() => {
        const parentId = searchParams.get('createSubAssetOf');
        if (!parentId) return;
        (async () => {
            try {
                const { data } = await api.get(`asset/get-by-id?id=${parentId}`);
                const parentAsset = data?.response;
                if (parentAsset) tog_large(null, parentAsset);
            } catch (err) {
                console.error('Failed to load parent asset for sub-asset create:', err);
            } finally {
                setSearchParams({});
            }
        })();
    }, [searchParams, setSearchParams]);

    // Deep link: open the edit form for a specific asset
    useEffect(() => {
        const editId = searchParams.get('editAsset');
        if (!editId) return;
        (async () => {
            try {
                const { data } = await api.get(`asset/get-by-id?id=${editId}`);
                const asset = data?.response;
                if (asset) tog_large(asset);
            } catch (err) {
                console.error('Failed to load asset for edit:', err);
            } finally {
                setSearchParams({});
            }
        })();
    }, [searchParams, setSearchParams]);

    // Fetch assets that don't have a UUID attached
    const fetchAssetsWithoutUuid = async () => {
        try {
            setAttachUuidData(prev => ({ ...prev, loading: true, error: '' }));
            const response = await api.get('/asset/all', { params: { hasUuid: false } });
            // Response is grouped by organization, flatten to get all assets
            const groupedData = response.data?.response || [];
            const assetsData = groupedData.flatMap(group => group.assets || []);
            setAssetsWithoutUuid(assetsData);
            setAttachUuidData(prev => ({ ...prev, loading: false }));
        } catch (err) {
            setAttachUuidData(prev => ({
                ...prev,
                loading: false,
                error: t("business.assets.error_loading", 'Failed to load assets. Please try again.')
            }));
        }
    };

    // Show confirmation before attaching UUID to asset
    const handleSelectAssetForAttachment = (asset) => {
        setAttachConfirmation({ show: true, asset });
    };

    // Confirm and attach UUID to selected asset
    const handleConfirmAttachUuid = async () => {
        const asset = attachConfirmation.asset;
        if (!asset) return;

        try {
            const uuidToNavigate = attachUuidData.uuid; // Capture UUID before state reset
            setAttachUuidData(prev => ({ ...prev, loading: true }));
            setAttachConfirmation({ show: false, asset: null });
            await api.put(`/asset/update`, {
                id: asset.id,
                uuid: uuidToNavigate
            });
            setAttachUuidModal(false);
            setAttachUuidData({ uuid: '', loading: false, error: '', searchTerm: '' });
            // Navigate to asset detail using captured UUID
            navigate(`/asset-detail/${uuidToNavigate}`);
        } catch (err) {
            setAttachUuidData(prev => ({
                ...prev,
                loading: false,
                error: err.response?.data?.message || t("business.assets.error_attaching_uuid", 'Failed to attach UUID. Please try again.')
            }));
        }
    };

    // Cancel attachment confirmation
    const handleCancelAttachConfirmation = () => {
        setAttachConfirmation({ show: false, asset: null });
    };

    // Filter assets without UUID based on search term
    const filteredAssetsWithoutUuid = assetsWithoutUuid.filter(asset => {
        const search = attachUuidData.searchTerm?.toLowerCase() || '';
        return asset.assetName?.toLowerCase().includes(search) ||
               asset.serialNumber?.toLowerCase().includes(search) ||
               asset.manufacturer?.toLowerCase().includes(search);
    });

    return (
        <div className='page-content mt-4'>
            <ImportResumeBanner job={assetImport.job} onResume={() => setFileModal(true)} />
            <div className="mb-4 d-flex align-items-center gap-3">
                <motion.div
                    className="mb-4 d-flex align-items-center gap-3"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    {!isRequestor && (
                        <PermissionGuard permission="asset:create">
                            <button
                                type="button"
                                className="btn btn-success"
                                onClick={() => tog_large()}
                                title={t("business.components.assets.add_new", "Add new asset")}
                            >
                                <FeatherIcon icon="plus" size={18} className="me-1" />
                                {t("business.assets.create_asset", "Create Asset")}
                            </button>
                        </PermissionGuard>
                    )}
                    <div className="ms-auto" style={{ minWidth: '400px', flexGrow: 1, maxWidth: '500px' }}>
                        <Input
                            type="text"
                            placeholder={t("business.assets.search_assets", "Search Assets...")}
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                </motion.div>
            </div>

            <AssetShortcuts
                organizationId={organization?.id}
                activeShortcut={activeShortcut}
                onShortcutClick={handleShortcutClick}
                t={t}
            />

            {/* Outcome of the last bulk transfer: how many moved, and what was left out. */}
            {bulkResult && (
                <Alert color="success" toggle={() => setBulkResult(null)}>
                    {bulkResult.pending
                        ? t("business.assets.bulk_transfer.success_requested", "Transfer request submitted for {{count}} asset(s); awaiting administrator approval.", { count: bulkResult.count })
                        : bulkResult.scheduled
                            ? t("business.assets.bulk_transfer.success_scheduled", "Transfer of {{count}} asset(s) scheduled.", { count: bulkResult.count })
                            : t("business.assets.bulk_transfer.success", "{{count}} asset(s) transferred.", { count: bulkResult.count })}
                    {bulkResult.skipped > 0 && (
                        <div className="small mt-1">
                            {t("business.assets.bulk_transfer.skipped_note", "{{count}} asset(s) were already at the destination and were skipped.", { count: bulkResult.skipped })}
                        </div>
                    )}
                </Alert>
            )}

            <Card>
                <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <h3 className="card-title text-success mb-0">{t("shared.assets", "Assets")}</h3>
                        <div className="d-flex align-items-center gap-2">
                            <button
                                className="btn btn-soft-primary p-2"
                                onClick={() => {
                                    setActiveShortcut(null);
                                    setFilterModal(true);
                                }}
                                title={t("business.components.assets.filter", "Filter assets")}
                            >
                                <FeatherIcon icon="filter" size={18} />
                            </button>
                            <button
                                className="btn btn-soft-info p-2"
                                onClick={() => setFileModal(true)}
                                title={t("business.components.assets.import_export", "Import/Export transactions")}
                            >
                                <FeatherIcon icon="file-text" size={18} />
                            </button>
                            {hasPermission(ASSET_TRANSFER) && organization?.id && (
                                <button
                                    className="btn btn-soft-success p-2"
                                    onClick={() => setShowTransferModal(true)}
                                    title={t("business.planning.transfer", "Transfer")}
                                >
                                    <FeatherIcon icon="repeat" size={18} />
                                </button>
                            )}
                        </div>
                    </div>
                </CardHeader>
                
                <CardBody>
                    {/* Bulk action bar — mirrors the Planning queue's selection bar. */}
                    {canBulkTransfer && (selectedCount > 0 || selectionNotice) && (
                        <div className="d-flex flex-column gap-2 mb-3 p-2 px-3 rounded bg-light border">
                            <div className="d-flex align-items-center gap-3">
                                {/* Just the count. The 200 cap is deliberately NOT shown here: as a
                                    trailing "/ 200" it reads as a denominator ("8 of 200"), implying
                                    the list holds 200 assets. The cap is stated in words at the only
                                    moments it applies — the two notices below. */}
                                <span className="fw-medium">
                                    {selectedCount} {t("shared.selected", "selected")}
                                </span>
                                <button
                                    className="btn btn-success btn-sm"
                                    onClick={() => setShowTransferModal(true)}
                                    disabled={selectedCount === 0}
                                >
                                    <FeatherIcon icon="repeat" size={14} className="me-1" />
                                    {t("business.assets.bulk_transfer.transfer_selected", "Transfer selected")}
                                </button>
                                <button className="btn btn-link btn-sm text-muted" onClick={clearSelection}>
                                    {t("shared.clear", "Clear")}
                                </button>
                            </div>
                            {/* Second step: widen the selection from the loaded rows to the whole
                                filter match. The total is only known after that fetch. */}
                            {allLoadedSelected && hasMore && !allMatchingSelected && !selectionNotice && (
                                <div className="small">
                                    {t("business.assets.bulk_transfer.all_loaded_selected", "All {{count}} loaded assets are selected.", { count: selectedCount })}
                                    {' '}
                                    <button
                                        className="btn btn-link btn-sm p-0 align-baseline"
                                        onClick={selectAllMatching}
                                        disabled={selectingAllMatching}
                                    >
                                        {selectingAllMatching && <Spinner size="sm" className="me-1" />}
                                        {selectingAllMatching
                                            ? t("business.assets.bulk_transfer.selecting_all_matching", "Selecting all matching assets...")
                                            : t("business.assets.bulk_transfer.select_all_matching", "Select all assets matching the current filter")}
                                    </button>
                                </div>
                            )}
                            {allMatchingSelected && (
                                <div className="small text-muted">
                                    {t("business.assets.bulk_transfer.all_matching_selected", "All {{count}} assets matching the current filter are selected.", { count: selectedCount })}
                                </div>
                            )}
                            {selectionNotice?.type === 'cap' && (
                                <div className="small text-warning">
                                    {t("business.assets.bulk_transfer.cap_reached", "You can transfer up to {{max}} assets at a time.", { max: BULK_TRANSFER_MAX_ASSETS })}
                                </div>
                            )}
                            {selectionNotice?.type === 'cap_matching' && (
                                <div className="small text-warning">
                                    {t("business.assets.bulk_transfer.cap_reached_matching", "The filter matches {{total}} assets; only the first {{max}} were selected. Narrow the filter to transfer the rest.", { total: selectionNotice.total, max: BULK_TRANSFER_MAX_ASSETS })}
                                </div>
                            )}
                        </div>
                    )}
                    <div
                        className="table-responsive"
                        style={{
                            overflowX: 'auto', 
                            whiteSpace: 'nowrap',
                             maxHeight: "calc(100vh - 300px)",
                            overflowY: "auto",
                        }}
                    >
                        <table className="table table-hover mb-0" style={{ 
                            borderSpacing: '0.1rem',
                            borderCollapse: 'separate'
                        }}>
                            <thead
                            style={{
                                position: "sticky",
                                top: 0,
                                background: "white",
                                zIndex: 1,
                                }}>
                                <tr>
                                    {canBulkTransfer && (
                                        <th style={{ width: '36px' }}>
                                            <input
                                                type="checkbox"
                                                className="form-check-input asset-select-check"
                                                checked={allLoadedSelected}
                                                ref={(el) => { if (el) el.indeterminate = someLoadedSelected; }}
                                                disabled={loading || assets.length === 0}
                                                onChange={toggleSelectLoaded}
                                                aria-label={t("shared.select_all", "Select All")}
                                            />
                                        </th>
                                    )}
                                    <th>#</th>
                                    <th>{t("shared.name", "Name")}</th>
                                    <th>{t("shared.asset_id", "Asset ID")}</th>
                                    <th>{t("shared.serial_number", "Serial Number")}</th>
                                    <th>{t("shared.asset_status", "Asset Status")}</th>
                                    <th>{t("shared.location", "Location")}</th>
                                    <th>{t("business.assets.table.qr_payload", "Qr Payload")}</th>
                                    {!isRequestor && <th>{t("shared.edit", "Edit")}</th>}
                                    {!isRequestor && <th>{t("shared.delete", "Delete")}</th>}
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence>
                                {assets?.map((asset, ind) => (
                                    <motion.tr
                                        key={asset?.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        {canBulkTransfer && (
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input asset-select-check"
                                                    checked={!!selectedById[asset.id]}
                                                    onChange={() => toggleRow(asset)}
                                                    aria-label={t("business.assets.bulk_transfer.select_row", "Select asset")}
                                                />
                                            </td>
                                        )}
                                        <th scope="row">{ind+1}</th>

                                        <td style={{ maxWidth: '220px' }}>
                                        <span
                                            className="text-primary cursor-pointer hover-underline text-truncate d-inline-block w-100"
                                            onClick={() => handleReportedByClick(asset?.id)}
                                            style={{ cursor: 'pointer' }}
                                            title={asset.assetName}
                                        >
                                            {asset.assetName}
                                        </span>
                                        {asset?.parent?.assetName && (
                                            <div
                                                className="text-muted small text-truncate"
                                                title={asset.parent.assetName}
                                                style={{ fontSize: '11px' }}
                                            >
                                                <i className="bx bx-sitemap me-1"></i>
                                                {t('business.assets.sub_asset_of', 'Sub-asset of: {{name}}', { name: asset.parent.assetName })}
                                            </div>
                                        )}
                                        {asset?.pendingCrossOrgTransfer && (
                                            <div
                                                className="text-warning-emphasis small text-truncate"
                                                title={t('business.planning.pending_outgoing_transfer', 'Transfer pending → {{organization}}', {
                                                    organization: asset.pendingCrossOrgTransfer.destinationOrganizationName || '',
                                                })}
                                                style={{ fontSize: '11px' }}
                                            >
                                                <i className="bx bx-transfer-alt me-1"></i>
                                                {t('business.planning.pending_outgoing_transfer', 'Transfer pending → {{organization}}', {
                                                    organization: asset.pendingCrossOrgTransfer.destinationOrganizationName || '',
                                                })}
                                            </div>
                                        )}
                                        {asset?.children?.length > 0 && (
                                            <div className="mt-1">
                                                <span
                                                    role="button"
                                                    onClick={(e) => { e.stopPropagation(); handleReportedByClick(asset.id, null, null, 'subAssets'); }}
                                                    className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill text-primary fw-medium"
                                                    style={{ background: 'rgba(13,110,253,0.08)', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                    title={t('business.assets.view_sub_assets_tooltip', 'View sub-assets')}
                                                >
                                                    {asset.children.length === 1
                                                        ? t('business.assets.n_sub_asset', '{{count}} Sub-Asset', { count: 1 })
                                                        : t('business.assets.n_sub_assets', '{{count}} Sub-Assets', { count: asset.children.length })}
                                                    <FeatherIcon icon="chevron-right" size={12} />
                                                </span>
                                            </div>
                                        )}
                                        </td>

                                        <td className="text-muted">{asset?.assetNumber || '-'}</td>

                                        <td className="text-muted">
                                            {asset?.serialNumber || '-'}
                                        </td>

                                        <td>
                                            {asset?.assetStatus ? (
                                                <span className={`badge ${getAssetStatusBadgeClass(asset.assetStatus)}`}>
                                                    {asset?.assetStatus ? t(`shared.${asset?.assetStatus.toLowerCase()}`, asset?.assetStatus).toUpperCase() : t("shared.n_a", "N/A")}

                                                    {asset.assetStatus === 'OFFLINE' && asset.downtimeReasonName ? ` (${t(`shared.${asset.downtimeReasonName.toLowerCase().replace(/\s+/g, '_')}`, asset.downtimeReasonName)})` : ''}
                                                </span>
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>

                                        <td>
                                        <span
                                        className="text-primary cursor-pointer hover-underline"
                                        onClick={() => handleDetailsClick('location', asset?.currentLocation, organization)}
                                        style={{ cursor: 'pointer' }}
                                        >
                                        {asset?.currentLocation}
                                        </span>
                                        </td>

                                        <td>
                                            <button
                                                type="button"
                                                className="btn btn-soft-success waves-effect waves-light"
                                                onClick={() => openQrModal(asset)}
                                                disabled={!asset.qrImageUrl}
                                            >
                                                {asset.qrImageUrl ? t("shared.view", 'View') : t("business.assets.not_generated", 'Not Generated')}
                                            </button>
                                        </td>

                                        {!isRequestor && (
                                            <td>
                                                <PermissionGuard permission="asset:update">
                                                    <span className='text-warning' role='button' onClick={() => tog_large(asset)}>
                                                        <FeatherIcon icon="edit" />
                                                    </span>
                                                </PermissionGuard>
                                            </td>
                                        )}
                                        {!isRequestor && (
                                            <td>
                                                {canDeleteAsset(asset) && (
                                                    <PermissionGuard permission="asset:delete">
                                                        <span className='text-danger' role='button' onClick={() => {
                                                            setSelectedEntry(asset);
                                                            setDeleteModal(true);
                                                        }}>
                                                            <FeatherIcon icon="trash-2" />
                                                        </span>
                                                    </PermissionGuard>
                                                )}
                                            </td>
                                        )}
                                    </motion.tr>
                                ))}
                                </AnimatePresence>
                            </tbody>
                        </table>
                        {loading && (
                            <div className="text-center py-3">
                                <Spinner color="primary" />
                            </div>
                        )}
                        </div>
                        <div className="d-flex justify-content-center mt-3">
                            {hasMore ? (
                                <Button 
                                    color="primary" 
                                    onClick={handleLoadMore}
                                    disabled={loading}
                                >
                                    {loading ? t("shared.loading", 'Loading...') : t("shared.load", 'Load More')}
                                </Button>
                            ) : (
                                <div className="text-muted py-2">
                                    {assets.length === 0 ? 
                                        (searchTerm
                                            ? t("business.assets.no_assets_match_search", 'No assets match your search')
                                            : (visibilityRestricted
                                                ? t("teams.restrictVisibility.emptyAssets", "No assets in your team's scope.")
                                                : t("shared.no_assets_found", 'No assets found'))) :
                                        t("business.assets.no_more_assets", 'No more assets to load')}
                                </div>
                            )}
                        </div>
                </CardBody>
            </Card>

            {/* Filter Modal */}
            <Modal
                isOpen={filterModal}
                toggle={() => setFilterModal(false)}
                centered
                className="fade-scale-modal"
                backdropClassName="modal-backdrop-blur"
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="modal-header text-white">
                        <h5 className="modal-title fs-4">{t("shared.filter_assets", "Filter Assets")}</h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={() => setFilterModal(false)}
                            aria-label={t("shared.close", "Close")}
                        ></button>
                    </div>

                    <div className="modal-body p-4">
                        <Row className="mb-4 g-3">
                            <Col md={12}>
                                <div className="form-group">
                                    <label htmlFor="filterLocation" className="form-label fw-medium">
                                        {t("shared.location", "Location")}
                                    </label>
                                    <select
                                        id="filterLocation"
                                        className="form-select"
                                        value={filters.location}
                                        onChange={(e) =>
                                            setFilters({ ...filters, location: e.target.value })
                                        }
                                    >
                                        <option value="">{t("shared.all_locations", "All Locations")}</option>
                                        {/* Value is the id, not the name: names are only unique within
                                            a parent, and a name saved with stray whitespace never
                                            resolves server-side. */}
                                        {flatLocations.map((loc) => (
                                            <option key={loc.id} value={String(loc.id)}>
                                                {loc.path}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </Col>
                        </Row>

                        <Row className="mb-4 g-3">
                            <Col md={12}>
                                <div className="form-group">
                                    <label htmlFor="filterManufacturer" className="form-label fw-medium">
                                        {t("shared.manufacturer", "Manufacturer")}
                                    </label>
                                    <select
                                        id="filterManufacturer"
                                        className="form-select"
                                        value={filters.manufacturer}
                                        onChange={(e) =>
                                            setFilters({ ...filters, manufacturer: e.target.value })
                                        }
                                    >
                                        <option value="">{t("business.assets.all_manufacturer", "All Manufacturer")}</option>
                                        {manufacturer?.map((item) => (
                                            <option key={item} value={item}>
                                                {item}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </Col>
                        </Row>

                        <Row className="mb-4 g-3">
                            <Col md={6}>
                                <div className="form-group">
                                    <label htmlFor="filterAssetStatus" className="form-label fw-medium">
                                        {t("shared.status", "Status")}
                                    </label>
                                    <select
                                        id="filterAssetStatus"
                                        className="form-select"
                                        value={filters.assetStatus}
                                        onChange={(e) =>
                                            setFilters({ ...filters, assetStatus: e.target.value })
                                        }
                                    >
                                        <option value="">{t("shared.all_statuses", "All Statuses")}</option>
                                        <option value="ONLINE">{t("shared.online", "Online")}</option>
                                        <option value="OFFLINE">{t("shared.offline", "Offline")}</option>
                                        <option value="DO_NOT_TRACK">{t("shared.do_not_track", "Do Not Track")}</option>
                                        <option value="DECOMMISSIONED">{t("shared.decommissioned", "Decommissioned")}</option>
                                    </select>
                                    <div className="form-check mt-2">
                                        <input
                                            type="checkbox"
                                            id="filterShowDecommissioned"
                                            className="form-check-input"
                                            checked={!!filters.showDecommissioned}
                                            onChange={(e) =>
                                                setFilters({ ...filters, showDecommissioned: e.target.checked })
                                            }
                                        />
                                        <label htmlFor="filterShowDecommissioned" className="form-check-label">
                                            {t("business.assets.show_decommissioned", "Show decommissioned")}
                                        </label>
                                    </div>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="form-group">
                                    <label htmlFor="filterCriticality" className="form-label fw-medium">
                                        {t("shared.criticality", "Criticality")}
                                    </label>
                                    <select
                                        id="filterCriticality"
                                        className="form-select"
                                        value={filters.criticality}
                                        onChange={(e) =>
                                            setFilters({ ...filters, criticality: e.target.value })
                                        }
                                    >
                                        <option value="">{t("shared.all_criticalities", "All Criticalities")}</option>
                                        {criticals?.map((c) => {
                                            const titled = c.charAt(0).toUpperCase() + c.slice(1).toLowerCase();
                                            return (
                                                <option key={c} value={c}>
                                                    {t(`shared.${c.toLowerCase()}`, titled)}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            </Col>
                        </Row>

                        <Row className="mb-4 g-3">
                            <Col md={6}>
                                <div className="form-group">
                                    <label htmlFor="filterAssetType" className="form-label fw-medium">
                                        {t("shared.asset_type", "Asset Type")}
                                    </label>
                                    <select
                                        id="filterAssetType"
                                        className="form-select"
                                        value={filters.assetTypeId}
                                        onChange={(e) =>
                                            setFilters({ ...filters, assetTypeId: e.target.value })
                                        }
                                    >
                                        <option value="">{t("shared.all_asset_types", "All Asset Types")}</option>
                                        {assetTypes?.map((at) => (
                                            <option key={at.id} value={at.id}>{at.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="form-group">
                                    <label htmlFor="filterVendor" className="form-label fw-medium">
                                        {t("shared.vendor", "Vendor")}
                                    </label>
                                    <select
                                        id="filterVendor"
                                        className="form-select"
                                        value={filters.vendorId}
                                        onChange={(e) =>
                                            setFilters({ ...filters, vendorId: e.target.value })
                                        }
                                    >
                                        <option value="">{t("shared.all_vendors", "All Vendors")}</option>
                                        {vendors?.map((v) => (
                                            <option key={v.id} value={v.id}>{v.vendorName}</option>
                                        ))}
                                    </select>
                                </div>
                            </Col>
                        </Row>

                        <Row className="mb-4 g-3">
                            <Col md={6}>
                                <div className="form-group">
                                    <label htmlFor="filterModelNumber" className="form-label fw-medium">
                                        {t("shared.model_number", "Model Number")}
                                    </label>
                                    <input
                                        id="filterModelNumber"
                                        type="text"
                                        className="form-control"
                                        value={filters.modelNumber}
                                        placeholder={t("shared.enter_model_number", "Enter model number")}
                                        onChange={(e) =>
                                            setFilters({ ...filters, modelNumber: e.target.value })
                                        }
                                    />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="form-group">
                                    <label htmlFor="filterHasTag" className="form-label fw-medium">
                                        {t("shared.tag", "Tag")}
                                    </label>
                                    <select
                                        id="filterHasTag"
                                        className="form-select"
                                        value={filters.hasUuid}
                                        onChange={(e) =>
                                            setFilters({ ...filters, hasUuid: e.target.value })
                                        }
                                    >
                                        <option value="">{t("shared.all", "All")}</option>
                                        <option value="true">{t("shared.with_tag", "With tag")}</option>
                                        <option value="false">{t("shared.without_tag", "Without tag")}</option>
                                    </select>
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div className="modal-footer bg-light border-top-0">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={clearFilters}
                        >
                            {t("shared.clear_filters", "Clear Filters")}
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setFilterModal(false)}
                        >
                            {t("shared.apply_filters", "Apply Filters")}
                        </button>
                    </div>
                </motion.div>
            </Modal>

            {/* Add/Edit Modal */}
            <Modal size="lg" isOpen={modal_large} toggle={() => tog_large()}>
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <div className="modal-header">
                        <h5 className="modal-title" id="myLargeModalLabel">
                            {selectedEntry?.id ? t("shared.buttons_edit_asset", 'Edit Asset') : t("business.assets.add_asset", 'Add Asset')}
                        </h5>
                        <button onClick={handleModalClose} type="button" className="btn-close" aria-label={t("shared.close", "Close")}></button>
                    </div>
                    
                    <form onSubmit={handleUpdate}>
                    <div className="modal-body">
                            {/* Name */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="assetName" className="form-label fw-medium">
                                            {t("shared.name", "Name")} <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="assetName"
                                            className={`form-control ${errors.assetName ? 'is-invalid' : ''}`}
                                            value={selectedEntry?.assetName || ''}
                                            onChange={(e) => setSelectedEntry({...selectedEntry, assetName: e.target.value})}
                                            placeholder={t("shared.enter_asset_name", "Enter asset name")}
                                        />
                                        {errors.assetName && <small className="text-danger">{errors.assetName}</small>}
                                    </div>
                                </Col>
                            </Row>

                            {/* Asset Status + Downtime Reason + Decommission fields */}
                            <AssetUsageStatusFields
                                usageStatus={selectedEntry?.assetStatus}
                                downtimeReasonId={selectedEntry?.downtimeReasonId}
                                decommissionedReason={selectedEntry?.decommissionedReason || ''}
                                decommissionedOn={selectedEntry?.decommissionedOn || ''}
                                currentEntityStatus={selectedEntry?.id ? selectedEntry?._persistedAssetStatus : null}
                                showDecommissioned
                                onChange={({ usageStatus, downtimeReasonId, decommissionedReason, decommissionedOn }) =>
                                    setSelectedEntry({
                                        ...selectedEntry,
                                        assetStatus: usageStatus,
                                        downtimeReasonId,
                                        decommissionedReason,
                                        decommissionedOn,
                                    })}
                                errors={errors}
                                variant="react-select"
                                layout="stacked"
                                label={t("shared.asset_status", "Asset Status")}
                                statusFieldName="assetStatus"
                                decommissionReasonFieldName="decommissionedReason"
                                selectStyles={selectStyles}
                                required
                            />

                            {/* Location */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="currentLocation" className="form-label fw-medium">
                                            {t("shared.location", "Location")} <span className="text-danger">*</span>
                                        </label>
                                        <Select
                                            id="currentLocation"
                                            options={flatLocations.map((loc) => ({
                                                value: loc.id,
                                                label: loc.name,
                                                path: loc.path
                                            }))}
                                            value={
                                                selectedEntry?.locationId
                                                    ? (() => {
                                                        const found = flatLocations.find(loc => loc.id == selectedEntry.locationId);
                                                        return found ? { value: found.id, label: found.name, path: found.path } : null;
                                                    })()
                                                    : null
                                            }
                                            onChange={(selected) => {
                                                setSelectedEntry({
                                                    ...selectedEntry,
                                                    locationId: selected?.value || ''
                                                });
                                            }}
                                            formatOptionLabel={(option) => (
                                                <div>
                                                    <div className="fw-medium">{option.label}</div>
                                                    {option.path && option.path !== option.label && (
                                                        <div className="text-muted small">{option.path}</div>
                                                    )}
                                                </div>
                                            )}
                                            filterOption={(option, inputValue) => {
                                                if (!inputValue) return true;
                                                const search = inputValue.toLowerCase();
                                                return option.data.label.toLowerCase().includes(search) ||
                                                    option.data.path.toLowerCase().includes(search);
                                            }}
                                            isClearable
                                            isSearchable
                                            isDisabled={!!selectedEntry?.id}
                                            placeholder={t("shared.search_and_select_location", "Search and select location...")}
                                            classNamePrefix="react-select"
                                            styles={selectStyles(!!errors.currentLocation)}
                                        />
                                        {errors.currentLocation && <small className="text-danger">{errors.currentLocation}</small>}
                                        {selectedEntry?.id && (
                                            <small className="text-muted d-block mt-1">
                                                {t("business.assets.location_change_via_transfer", "To move this asset, use the Transfer action on the asset page.")}
                                            </small>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            {/* Parent Asset (optional) */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="parentAsset" className="form-label fw-medium">
                                            {t("business.assets.parent_asset", "Parent Asset")}
                                            <span className="text-muted ms-2 small">
                                                {t("shared.optional", "(Optional)")}
                                            </span>
                                        </label>
                                        <ParentAssetSelector
                                            value={selectedEntry?.parent || null}
                                            onChange={(asset) => setSelectedEntry({ ...selectedEntry, parent: asset })}
                                            excludeIds={selectedEntry?.id ? [selectedEntry.id] : []}
                                            selectStyles={selectStyles}
                                        />
                                        <small className="text-muted">
                                            {t("business.assets.parent_asset_hint", "Choose a parent to make this a sub-asset, or leave empty for a root asset.")}
                                        </small>
                                    </div>
                                </Col>
                            </Row>

                            {/* Asset ID */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="assetNumber" className="form-label fw-medium">
                                            {t("business.assets.asset_id", "Asset ID")}
                                        </label>
                                        <div className="position-relative">
                                            <input
                                                type="text"
                                                id="assetNumber"
                                                className={`form-control ${
                                                    errors.assetNumber ? 'is-invalid' :
                                                    assetNumberValidation.isValid === true ? 'is-valid' :
                                                    assetNumberValidation.isValid === false ? 'is-invalid' : ''
                                                }`}
                                                value={selectedEntry?.assetNumber || ''}
                                                onChange={(e) => handleAssetNumberChange(e.target.value)}
                                                placeholder={t("shared.enter_asset_id", "Enter asset ID (auto-generated if empty)")}
                                            />
                                            {assetNumberValidation.isValidating && (
                                                <div className="position-absolute" style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                                                    <Spinner size="sm" color="primary" />
                                                </div>
                                            )}
                                        </div>
                                        {errors.assetNumber && <small className="text-danger">{errors.assetNumber}</small>}
                                        {!errors.assetNumber && assetNumberValidation.message && (
                                            <>
                                                <small className={assetNumberValidation.isValid ? 'text-success' : 'text-warning'}>
                                                    {assetNumberValidation.message}
                                                </small>
                                                {assetNumberValidation.duplicateAssets && assetNumberValidation.duplicateAssets.length > 0 && (
                                                    <div className="mt-2 p-2 border border-warning rounded bg-light">
                                                        <small className="d-block mb-2">
                                                            <strong>{t("business.assets.existing_assets_with_id", "Existing Assets with this ID")}</strong>
                                                            {assetNumberValidation.duplicateCount > 0 && (
                                                                <span className="text-muted">
                                                                    {' '}({t("business.assets.showing_x_of_y", "showing {{x}} of {{y}}", { x: Math.min(5, assetNumberValidation.duplicateAssets.length), y: assetNumberValidation.duplicateCount })})
                                                                </span>
                                                            )}
                                                            :
                                                        </small>
                                                        <ul className="mb-0 ps-3">
                                                            {assetNumberValidation.duplicateAssets.slice(0, 5).map((asset, index) => (
                                                                <li key={asset.id || index}>
                                                                    <small>
                                                                        {asset.assetName || t("shared.n_a", 'N/A')} - {asset.serialNumber || t("shared.n_a", 'N/A')}
                                                                    </small>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                        {assetNumberValidation.duplicateCount > 5 && (
                                                            <small className="d-block mt-1 text-muted">
                                                                +{assetNumberValidation.duplicateCount - 5} {t("shared.more", "more...")}
                                                            </small>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            {/* Serial Number */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="serialNumber" className="form-label fw-medium">
                                            {t("shared.serial_number", "Serial Number")}
                                        </label>
                                        <input
                                            type="text"
                                            id="serialNumber"
                                            className={`form-control ${errors.serialNumber ? 'is-invalid' : ''}`}
                                            value={selectedEntry?.serialNumber || ''}
                                            onChange={(e) => setSelectedEntry({...selectedEntry, serialNumber: e.target.value})}
                                            placeholder={t("shared.enter_serial_number", "Enter serial number")}
                                        />
                                        {errors.serialNumber && <small className="text-danger">{errors.serialNumber}</small>}
                                    </div>
                                </Col>
                            </Row>

                            {/* Criticality */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="criticality" className="form-label fw-medium">
                                            {t("shared.criticality", "Criticality")}
                                        </label>
                                        <Select
                                            id="criticality"
                                            options={criticals?.map((c) => ({
                                                value: c,
                                                label: t(`shared.${c.toLowerCase()}`, c)
                                            })) || []}
                                            value={
                                                selectedEntry?.criticality
                                                    ? { value: selectedEntry.criticality, label: t(`shared.${selectedEntry.criticality.toLowerCase()}`, selectedEntry.criticality) }
                                                    : null
                                            }
                                            onChange={(selected) => setSelectedEntry({...selectedEntry, criticality: selected?.value || ''})}
                                            isClearable
                                            isSearchable
                                            placeholder={t("shared.select_criticality", 'Select Criticality')}
                                            classNamePrefix="react-select"
                                            styles={selectStyles(!!errors.criticality)}
                                        />
                                        {errors.criticality && <small className="text-danger">{errors.criticality}</small>}
                                    </div>
                                </Col>
                            </Row>

                            {/* Manufacturer */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="manufacturer" className="form-label fw-medium">
                                            {t("shared.manufacturer", "Manufacturer")}
                                        </label>
                                        <input
                                            type="text"
                                            id="manufacturer"
                                            className={`form-control ${errors.manufacturer ? 'is-invalid' : ''}`}
                                            value={selectedEntry?.manufacturer || ''}
                                            onChange={(e) => setSelectedEntry({...selectedEntry, manufacturer: e.target.value})}
                                            placeholder={t("shared.enter_manufacturer", "Enter manufacturer")}
                                        />
                                        {errors.manufacturer && <small className="text-danger">{errors.manufacturer}</small>}
                                    </div>
                                </Col>
                            </Row>

                            {/* Model Number */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="modelNumber" className="form-label fw-medium">
                                            {t("shared.model_number", "Model Number")}
                                        </label>
                                        <input
                                            type="text"
                                            id="modelNumber"
                                            className={`form-control ${errors.modelNumber ? 'is-invalid' : ''}`}
                                            value={selectedEntry?.modelNumber || ''}
                                            onChange={(e) => setSelectedEntry({...selectedEntry, modelNumber: e.target.value})}
                                            placeholder={t("shared.enter_model_number", "Enter model number")}
                                        />
                                        {errors.modelNumber && <small className="text-danger">{errors.modelNumber}</small>}
                                    </div>
                                </Col>
                            </Row>

                            {/* Asset Types */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="assetTypeIds" className="form-label fw-medium">
                                            {t("shared.asset_types", "Asset Types")}
                                        </label>
                                        <Select
                                            isMulti
                                            id="assetTypeIds"
                                            options={assetTypes.map(at => ({ value: at.id, label: at.name }))}
                                            value={(selectedEntry?.assetTypeIds || []).map(id => {
                                                const at = assetTypes.find(a => a.id === id);
                                                return at ? { value: at.id, label: at.name } : null;
                                            }).filter(Boolean)}
                                            onChange={(selected) => setSelectedEntry({
                                                ...selectedEntry,
                                                assetTypeIds: selected ? selected.map(s => s.value) : []
                                            })}
                                            styles={multiSelectStyles(false)}
                                            placeholder={t("shared.select_asset_types", "Select asset types...")}
                                            isClearable
                                        />
                                    </div>
                                </Col>
                            </Row>

                            {/* Asset Images */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label className="form-label fw-medium">{t("shared.asset_images", "Asset Images")}</label>
                                        <div
                                            className="border rounded p-4 text-center"
                                            style={{ borderStyle: 'dashed', borderWidth: '2px', borderColor: '#dee2e6', backgroundColor: '#fafbfc' }}
                                        >
                                            <input
                                                type="file"
                                                id="assetImageUploadInput"
                                                className="d-none"
                                                accept="image/*"
                                                multiple
                                                onChange={handleFileChange}
                                                disabled={imageUploading}
                                            />
                                            <label
                                                htmlFor="assetImageUploadInput"
                                                className={`btn btn-outline-primary mb-2 ${imageUploading ? 'disabled' : ''}`}
                                                style={{ cursor: imageUploading ? 'not-allowed' : 'pointer' }}
                                            >
                                                {imageUploading ? (
                                                    <><Spinner size="sm" className="me-2" />{t("shared.uploading", "Uploading...")}</>
                                                ) : (
                                                    <><FeatherIcon icon="upload" size={16} className="me-2" />{t("shared.upload_images", "Upload Images")}</>
                                                )}
                                            </label>
                                            <p className="text-muted small mb-0">
                                                {imageUploading ? t("shared.wait_uploading_images", 'Please wait while images are being uploaded...') : t("shared.click_to_select_images", 'Click to select images to upload')}
                                            </p>
                                        </div>
                                        {/* Display existing/uploaded images */}
                                        {selectedEntry?.assetImageUrls?.length > 0 && (
                                            <div className="mt-3">
                                                <small className="text-muted d-block mb-2">{t("business.assets.current_images", "Current Images:")}</small>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {selectedEntry.assetImageUrls.map((url, index) => (
                                                        <div key={index} className="position-relative">
                                                            <img
                                                                src={url}
                                                                alt={`Asset ${index + 1}`}
                                                                className="rounded border"
                                                                style={{ width: '80px', height: '80px', objectFit: 'cover', cursor: 'pointer' }}
                                                                onClick={() => window.open(url, '_blank')}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-danger btn-sm position-absolute"
                                                                style={{ top: '-8px', right: '-8px', padding: '2px 6px', borderRadius: '50%' }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setDeleteConfirmation({
                                                                        show: true,
                                                                        index: index,
                                                                        imageUrl: url
                                                                    });
                                                                }}
                                                            >
                                                                <FeatherIcon icon="x" size={12} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            {/* Documents */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label className="form-label fw-medium">{t("shared.documents", "Documents")}</label>
                                        <div
                                            className="border rounded p-4 text-center"
                                            style={{ borderStyle: 'dashed', borderWidth: '2px', borderColor: '#dee2e6', backgroundColor: '#fafbfc' }}
                                        >
                                            <input
                                                type="file"
                                                id="assetDocUploadInput"
                                                className="d-none"
                                                accept=".pdf,.doc,.docx,.xls,.xlsx"
                                                multiple
                                                onChange={handleDocumentChange}
                                                disabled={docUploading}
                                            />
                                            <label
                                                htmlFor="assetDocUploadInput"
                                                className={`btn btn-outline-secondary mb-2 ${docUploading ? 'disabled' : ''}`}
                                                style={{ cursor: docUploading ? 'not-allowed' : 'pointer' }}
                                            >
                                                {docUploading ? (
                                                    <><Spinner size="sm" className="me-2" />{t("shared.uploading", "Uploading...")}</>
                                                ) : (
                                                    <><FeatherIcon icon="paperclip" size={16} className="me-2" />{t("business.assets.attach_documents", "Attach Documents")}</>
                                                )}
                                            </label>
                                            <p className="text-muted small mb-0">
                                                {docUploading ? t("business.assets.uploading_documents_wait", 'Please wait while documents are being uploaded...') : t("business.assets.pdf_word_excel", 'PDF, Word, Excel files (max 50MB)')}
                                            </p>
                                        </div>
                                        {/* Display existing/uploaded documents */}
                                        {selectedEntry?.documentUrls?.length > 0 && (
                                            <div className="mt-3">
                                                <small className="text-muted d-block mb-2">{t("shared.current_documents", "Current Documents:")}</small>
                                                <div className="d-flex flex-column gap-2">
                                                    {selectedEntry.documentUrls.map((url, index) => (
                                                        <div key={index} className="d-flex align-items-center p-2 bg-light rounded">
                                                            <div className="d-flex align-items-center justify-content-center me-2"
                                                                 style={{ width: '32px', height: '32px', backgroundColor: '#e9ecef', borderRadius: '4px' }}>
                                                                <FeatherIcon icon={getDocumentIcon(url)} size={16} className="text-primary" />
                                                            </div>
                                                            <div className="flex-grow-1 overflow-hidden">
                                                                <p className="mb-0 text-truncate small fw-medium">{getFileNameFromUrl(url)}</p>
                                                                <small className="text-muted">{url.split('.').pop()?.toUpperCase()}</small>
                                                            </div>
                                                            <div className="d-flex gap-1">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-primary btn-sm"
                                                                    onClick={() => window.open(url, '_blank')}
                                                                >
                                                                    <FeatherIcon icon="download" size={12} />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-danger btn-sm"
                                                                    onClick={() => handleDeleteDocument(index)}
                                                                >
                                                                    <FeatherIcon icon="trash-2" size={12} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            {/* Custom Fields */}
                            {customFieldDefs.length > 0 && (
                                <Row className="mb-4">
                                    <Col md={12}>
                                        <div className="form-group">
                                            <label className="form-label fw-medium">{t("shared.custom_fields", "Custom Fields")}</label>
                                            <Row>
                                                {customFieldDefs.map((def) => (
                                                    <Col md={6} key={def.id}>
                                                        <DynamicCustomFieldRenderer
                                                            definition={def}
                                                            value={customFieldValues[def.id]}
                                                            onChange={(val) => setCustomFieldValues((prev) => ({ ...prev, [def.id]: val }))}
                                                            error={errors[`cf_${def.id}`]}
                                                        />
                                                    </Col>
                                                ))}
                                            </Row>
                                        </div>
                                    </Col>
                                </Row>
                            )}

                            {/* QR Code */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label className="form-label fw-medium">{t("shared.qr_code", "QR Code")}</label>
                                        <div className="d-flex align-items-center gap-3">
                                            <button
                                                type="button"
                                                onClick={handleQr}
                                                className="btn btn-success btn-rounded waves-effect waves-light"
                                            >
                                                {selectedEntry?.qrImageUrl ? t("business.assets.regenerate_qr", 'Regenerate QR') : t("business.assets.generate_qr_code", 'Generate QR Code')}
                                            </button>

                                            {qrGenerationSuccess && (
                                                <Alert color="success" className="mb-0 py-1 px-2">
                                                    {t("business.assets.qr_success", "QR generated successfully!")}
                                                </Alert>
                                            )}

                                            {(selectedEntry?.qrPayload || selectedEntry?.qrImageUrl) && (
                                                <div
                                                    className="position-relative"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                        setQrModal(true);
                                                        setQrGenerationSuccess(false);
                                                    }}
                                                >
                                                    <img
                                                        src={selectedEntry.qrImageUrl || `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${selectedEntry.qrPayload}`}
                                                        alt="QR Preview"
                                                        style={{
                                                            width: '50px',
                                                            height: '50px',
                                                            border: '1px solid #dee2e6',
                                                            borderRadius: '4px'
                                                        }}
                                                    />
                                                    <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                        style={{
                                                            backgroundColor: 'rgba(0,0,0,0.2)',
                                                            borderRadius: '4px',
                                                            opacity: 0,
                                                            transition: 'opacity 0.2s',
                                                            ':hover': {
                                                                opacity: 1
                                                            }
                                                        }}
                                                    >
                                                        <FeatherIcon icon="maximize-2" size={16} color="white" />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        {errors.qr && <small className="text-danger">{errors.qr}</small>}
                                    </div>
                                </Col>
                            </Row>

                            {/* Description */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="description" className="form-label fw-medium">
                                            {t("shared.description", "Description")}
                                        </label>
                                        <textarea
                                            id="description"
                                            rows="3"
                                            className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                                            value={selectedEntry?.description || ''}
                                            onChange={(e) => setSelectedEntry({ ...selectedEntry, description: e.target.value })}
                                            placeholder={t("shared.enter_description", "Enter description")}
                                        />
                                        {errors.description && <small className="text-danger">{errors.description}</small>}
                                    </div>
                                </Col>
                            </Row>

                            {/* Vendor */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="vendor" className="form-label fw-medium">
                                            {t("shared.vendor", "Vendor")}
                                        </label>
                                        <Select
                                            id="vendor"
                                            options={vendors?.map((vendor) => ({
                                                value: vendor.id,
                                                label: vendor.vendorName
                                            })) || []}
                                            value={
                                                selectedEntry?.vendorId
                                                    ? vendors?.find(v => v.id == selectedEntry.vendorId)
                                                        ? { value: selectedEntry.vendorId, label: vendors.find(v => v.id == selectedEntry.vendorId)?.vendorName }
                                                        : null
                                                    : null
                                            }
                                            onChange={(selected) => setSelectedEntry({ ...selectedEntry, vendorId: selected?.value || '' })}
                                            isClearable
                                            isSearchable
                                            placeholder={t("business.assets.search_vendor", "Search and select vendor...")}
                                            classNamePrefix="react-select"
                                            styles={selectStyles(!!errors.vendorId)}
                                        />
                                        {errors.vendorId && <small className="text-danger">{errors.vendorId}</small>}
                                    </div>
                                </Col>
                            </Row>

                            {/* Currency — moved out of the Asset Cost row so it's always visible and
                                can label the Average Price by Case field below. */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="currency" className="form-label fw-medium">
                                            {t("shared.currency", "Currency")}
                                        </label>
                                        {(() => {
                                            // Currency is org-level — sourced from the asset's parent org (on edit) or
                                            // the active org's setting (on create). Disabled here so it can't be set
                                            // per-asset; change it under Organization Settings instead.
                                            const orgCurrency = selectedEntry?.organization?.currency
                                                || currentUser?.activeOrganizationCurrency
                                                || 'USD';
                                            return (
                                                <Select
                                                    id="currency"
                                                    options={Object.entries(currencies(t)).map(([code, label]) => ({
                                                        value: code,
                                                        label,
                                                    }))}
                                                    value={{ value: orgCurrency, label: currencies(t)[orgCurrency] || orgCurrency }}
                                                    isDisabled
                                                    classNamePrefix="react-select"
                                                    styles={selectStyles(false)}
                                                />
                                            );
                                        })()}
                                        <small className="text-muted">
                                            {t(
                                                "shared.currency_org_locked_hint",
                                                "Set under Organization Settings — applies to every asset, part, and tool in the organization."
                                            )}
                                        </small>
                                    </div>
                                </Col>
                            </Row>

                            {/* Downtime cost estimates — used by the Downtime Cost report */}
                            <Row className="mb-4">
                                <Col md={6}>
                                    <div className="form-group">
                                        <label htmlFor="averagePricePerCase" className="form-label fw-medium">
                                            {t("business.assets.average_price_per_case", "Average price by case")}
                                            <span className="text-muted ms-1">({selectedEntry?.organization?.currency || currentUser?.activeOrganizationCurrency || 'USD'})</span>
                                        </label>
                                        <input
                                            type="number"
                                            id="averagePricePerCase"
                                            step="0.01"
                                            min="0"
                                            className={`form-control ${errors.averagePricePerCase ? 'is-invalid' : ''}`}
                                            value={selectedEntry?.averagePricePerCase ?? ''}
                                            onChange={(e) => setSelectedEntry({ ...selectedEntry, averagePricePerCase: e.target.value })}
                                            placeholder={t("business.assets.average_price_per_case_placeholder", "e.g. 100")}
                                        />
                                        {errors.averagePricePerCase && <small className="text-danger">{errors.averagePricePerCase}</small>}
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="form-group">
                                        <label htmlFor="averageDurationPerCaseHours" className="form-label fw-medium">
                                            {t("business.assets.average_duration_per_case", "Average duration by case (hours)")}
                                        </label>
                                        <input
                                            type="number"
                                            id="averageDurationPerCaseHours"
                                            step="0.1"
                                            min="0.1"
                                            className={`form-control ${errors.averageDurationPerCaseHours ? 'is-invalid' : ''}`}
                                            value={selectedEntry?.averageDurationPerCaseHours ?? ''}
                                            onChange={(e) => setSelectedEntry({ ...selectedEntry, averageDurationPerCaseHours: e.target.value })}
                                            placeholder={t("business.assets.average_duration_per_case_placeholder", "e.g. 0.5")}
                                        />
                                        {errors.averageDurationPerCaseHours && <small className="text-danger">{errors.averageDurationPerCaseHours}</small>}
                                    </div>
                                </Col>
                            </Row>

                            {/* Acquisition Type */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="acquisitionType" className="form-label fw-medium">
                                            {t("business.assets.acquisition_type", "Acquisition Type")}
                                        </label>
                                        <Select
                                            id="acquisitionType"
                                            options={[
                                                { value: 'PURCHASED', label: t("business.assets.purchased", "Purchased") },
                                                { value: 'RENTAL', label: t("business.assets.rental", "Rental") },
                                                { value: 'DEMO', label: t("business.assets.demo", "Demo") },
                                                { value: 'PLACEMENT', label: t("business.assets.placement", "Placement") },
                                                { value: 'DONATION', label: t("business.assets.donation", "Donation/Grant") },
                                            ]}
                                            value={
                                                selectedEntry?.acquisitionType
                                                    ? {
                                                        value: selectedEntry.acquisitionType,
                                                        label: {
                                                            'PURCHASED': t("business.assets.purchased", "Purchased"),
                                                            'RENTAL': t("business.assets.rental", "Rental"),
                                                            'DEMO': t("business.assets.demo", "Demo"),
                                                            'PLACEMENT': t("business.assets.placement", "Placement"),
                                                            'DONATION': t("business.assets.donation", "Donation/Grant"),
                                                        }[selectedEntry.acquisitionType] || selectedEntry.acquisitionType
                                                    }
                                                    : { value: 'PURCHASED', label: t("business.assets.purchased", "Purchased") }
                                            }
                                            onChange={(selected) => {
                                                const newType = selected?.value || 'PURCHASED';
                                                const oldType = selectedEntry?.acquisitionType || 'PURCHASED';
                                                const updates = { ...selectedEntry, acquisitionType: newType };
                                                // Clear fields that don't apply to the new type
                                                if (newType !== oldType) {
                                                    if (newType === 'PURCHASED' || newType === 'DONATION') {
                                                        updates.acquisitionStartDate = '';
                                                        updates.acquisitionEndDate = '';
                                                    }
                                                    if (newType !== 'PURCHASED') {
                                                        updates.yearOfPurchase = '';
                                                        updates.assetCost = '';
                                                        updates.currency = 'USD';
                                                        updates.commissioningDate = '';
                                                        updates.estimatedLifespan = '';
                                                        updates.acceleratedDepreciation = false;
                                                        updates.depreciationPeriodYears = '';
                                                    }
                                                }
                                                setSelectedEntry(updates);
                                            }}
                                            isSearchable={false}
                                            classNamePrefix="react-select"
                                            styles={selectStyles(false)}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            {/* RENTAL / DEMO / PLACEMENT — Start & End Dates */}
                            {['RENTAL', 'DEMO', 'PLACEMENT'].includes(selectedEntry?.acquisitionType) && (
                                <Row className="mb-4 g-3">
                                    <Col md={6}>
                                        <div className="form-group">
                                            <label className="form-label fw-medium">
                                                {{ 'RENTAL': t("business.assets.rental_start_date", "Rental Start Date"),
                                                   'DEMO': t("business.assets.demo_start_date", "Demo Start Date"),
                                                   'PLACEMENT': t("business.assets.placement_start_date", "Placement Start Date"),
                                                }[selectedEntry.acquisitionType]}
                                            </label>
                                            <DatePicker
                                                selected={selectedEntry?.acquisitionStartDate ? (() => {
                                                    const val = selectedEntry.acquisitionStartDate;
                                                    if (typeof val === 'string' && val.includes('-') && val.split('-').length === 3) {
                                                        const parts = val.split('-');
                                                        if (parts[0].length === 2) return new Date(parts[2], parts[1] - 1, parts[0]);
                                                        return new Date(parts[0], parts[1] - 1, parts[2]);
                                                    }
                                                    return new Date(val);
                                                })() : null}
                                                onChange={(date) => {
                                                    if (date) {
                                                        const day = String(date.getDate()).padStart(2, '0');
                                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                                        const year = date.getFullYear();
                                                        setSelectedEntry({ ...selectedEntry, acquisitionStartDate: `${day}-${month}-${year}` });
                                                    } else {
                                                        setSelectedEntry({ ...selectedEntry, acquisitionStartDate: '' });
                                                    }
                                                }}
                                                dateFormat="MMMM d, yyyy"
                                                className="form-control"
                                                wrapperClassName="w-100"
                                                showMonthDropdown
                                                showYearDropdown
                                                dropdownMode="select"
                                                placeholderText={t("shared.select_date", "Select date")}
                                                isClearable
                                            />
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="form-group">
                                            <label className="form-label fw-medium">
                                                {{ 'RENTAL': t("business.assets.rental_end_date", "Rental End Date"),
                                                   'DEMO': t("business.assets.demo_end_date", "Demo End Date"),
                                                   'PLACEMENT': t("business.assets.placement_end_date", "Placement End Date"),
                                                }[selectedEntry.acquisitionType]}
                                            </label>
                                            <DatePicker
                                                selected={selectedEntry?.acquisitionEndDate ? (() => {
                                                    const val = selectedEntry.acquisitionEndDate;
                                                    if (typeof val === 'string' && val.includes('-') && val.split('-').length === 3) {
                                                        const parts = val.split('-');
                                                        if (parts[0].length === 2) return new Date(parts[2], parts[1] - 1, parts[0]);
                                                        return new Date(parts[0], parts[1] - 1, parts[2]);
                                                    }
                                                    return new Date(val);
                                                })() : null}
                                                onChange={(date) => {
                                                    if (date) {
                                                        const day = String(date.getDate()).padStart(2, '0');
                                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                                        const year = date.getFullYear();
                                                        setSelectedEntry({ ...selectedEntry, acquisitionEndDate: `${day}-${month}-${year}` });
                                                    } else {
                                                        setSelectedEntry({ ...selectedEntry, acquisitionEndDate: '' });
                                                    }
                                                }}
                                                dateFormat="MMMM d, yyyy"
                                                className="form-control"
                                                wrapperClassName="w-100"
                                                showMonthDropdown
                                                showYearDropdown
                                                dropdownMode="select"
                                                placeholderText={t("shared.select_date", "Select date")}
                                                isClearable
                                            />
                                        </div>
                                    </Col>
                                </Row>
                            )}

                            {/* PURCHASED fields (also shown for null/empty for backward compat) */}
                            {(!selectedEntry?.acquisitionType || selectedEntry?.acquisitionType === 'PURCHASED') && (<>
                            {/* Year of Purchase */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="year-of-purchase" className="form-label fw-medium">
                                            {t("shared.year_of_purchase", "Year of Purchase")}
                                        </label>
                                        <input
                                            type="number"
                                            id="year-of-purchase"
                                            className="form-control"
                                            min="1900"
                                            value={selectedEntry?.yearOfPurchase || ''}
                                            max={new Date().getFullYear()}
                                            placeholder={t("business.assets.enter_year", "Enter year")}
                                            onChange={(e) => setSelectedEntry({...selectedEntry, yearOfPurchase: e.target.value})}
                                        />
                                        {errors?.yearOfPurchase && <small className="text-danger">{errors.yearOfPurchase}</small>}
                                    </div>
                                </Col>
                            </Row>

                            {/* Asset Cost — full width (currency selector lives above, outside the
                                PURCHASED conditional, so it's shared with Average Price by Case) */}
                            <Row className="mb-4 g-3">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="assetCost" className="form-label fw-medium">
                                            {t("business.assets.asset_cost", "Asset Cost")}
                                            <span className="text-muted ms-1">({selectedEntry?.organization?.currency || currentUser?.activeOrganizationCurrency || 'USD'})</span>
                                        </label>
                                        <input
                                            type="number"
                                            id="assetCost"
                                            step="0.01"
                                            className={`form-control ${errors.assetCost ? 'is-invalid' : ''}`}
                                            value={selectedEntry?.assetCost || ''}
                                            onChange={(e) => setSelectedEntry({ ...selectedEntry, assetCost: e.target.value })}
                                            placeholder={t("business.assets.enter_asset_cost", "Enter asset cost")}
                                        />
                                        {errors.assetCost && <small className="text-danger">{errors.assetCost}</small>}
                                    </div>
                                </Col>
                            </Row>

                            {/* Commissioning Date + Estimated Lifespan */}
                            <Row className="mb-4 g-3">
                                <Col md={6}>
                                    <div className="form-group">
                                        <label htmlFor="commissioningDate" className="form-label fw-medium">
                                            {t("shared.commissioning_date", "Commissioning Date")}
                                        </label>
                                        <DatePicker                                       
                                            id="commissioningDate"
                                            selected={selectedEntry?.commissioningDate ? (() => {
                                                const val = selectedEntry.commissioningDate;
                                                if (val.includes('-') && val.split('-').length === 3) {
                                                    const parts = val.split('-');
                                                    if (parts[0].length === 2) {
                                                        // DD-MM-YYYY format
                                                        return new Date(parts[2], parts[1] - 1, parts[0]);
                                                    }
                                                    // YYYY-MM-DD format
                                                    return new Date(parts[0], parts[1] - 1, parts[2]);
                                                }
                                                return new Date(val);
                                            })() : null}
                                            onChange={(date) => {
                                                if (date) {
                                                    const day = String(date.getDate()).padStart(2, '0');
                                                    const month = String(date.getMonth() + 1).padStart(2, '0');
                                                    const year = date.getFullYear();
                                                    setSelectedEntry({ ...selectedEntry, commissioningDate: `${day}-${month}-${year}` });
                                                } else {
                                                    setSelectedEntry({ ...selectedEntry, commissioningDate: '' });
                                                }
                                            }}
                                            dateFormat="MMMM d, yyyy"
                                            className={`form-control ${errors.commissioningDate ? 'is-invalid' : ''}`}
                                            wrapperClassName="w-100"
                                            showMonthDropdown
                                            showYearDropdown
                                            dropdownMode="select"
                                            placeholderText={t("shared.select_date", "Select date")}
                                            isClearable
                                        />
                                        {errors.commissioningDate && <small className="text-danger">{errors.commissioningDate}</small>}
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="form-group">
                                        <label htmlFor="estimatedLifespan" className="form-label fw-medium">
                                            {t("business.assets.estimated_lifespan_years", "Estimated Lifespan (Years)")}
                                        </label>
                                        <input
                                            type="number"
                                            id="estimatedLifespan"
                                            min="1"
                                            className={`form-control ${errors.estimatedLifespan ? 'is-invalid' : ''}`}
                                            value={selectedEntry?.estimatedLifespan || ''}
                                            onChange={(e) => setSelectedEntry({ ...selectedEntry, estimatedLifespan: e.target.value })}
                                            placeholder={t("business.assets.enter_estimated_lifespan", "Enter estimated lifespan in years")}
                                        />
                                        {errors.estimatedLifespan && <small className="text-danger">{errors.estimatedLifespan}</small>}
                                    </div>
                                </Col>
                            </Row>

                            {/* Slice 6: per-asset replacement threshold (% of purchase price).
                                Default 50%; the badge on the TCO panel / rollup card fires when
                                cumulative maintenance reaches the threshold. */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label htmlFor="replacementThresholdPercent" className="form-label fw-medium">
                                            {t("cost_tracking.asset_tco.threshold_label", "Replacement threshold (%)")}
                                        </label>
                                        <input
                                            type="number"
                                            id="replacementThresholdPercent"
                                            min="0"
                                            max="500"
                                            step="0.01"
                                            className="form-control"
                                            value={selectedEntry?.replacementThresholdPercent ?? ''}
                                            onChange={(e) => setSelectedEntry({ ...selectedEntry, replacementThresholdPercent: e.target.value })}
                                            placeholder="50"
                                        />
                                        <small className="text-muted">
                                            {t("cost_tracking.asset_tco.threshold_hint",
                                                "When cumulative maintenance reaches this percent of purchase price, the asset is flagged for replacement. Default 50%.")}
                                        </small>
                                    </div>
                                </Col>
                            </Row>

                            {/* Accelerated Depreciation */}
                            <Row className="mb-4">
                                <Col md={12}>
                                    <div className="form-group">
                                        <label className="form-label fw-medium">
                                            {t("shared.accelerated_depreciation", "Accelerated Depreciation")}
                                        </label>
                                        <div className="form-check mt-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="acceleratedDepreciation"
                                                checked={selectedEntry?.acceleratedDepreciation || false}
                                                onChange={(e) => setSelectedEntry({
                                                    ...selectedEntry,
                                                    acceleratedDepreciation: e.target.checked,
                                                    depreciationPeriodYears: e.target.checked ? selectedEntry?.depreciationPeriodYears : ''
                                                })}
                                            />
                                            <label className="form-check-label" htmlFor="acceleratedDepreciation">
                                                {t("business.assets.use_shorter_depreciation", "Use shorter depreciation period")}
                                            </label>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            {/* Depreciation Period (conditional) */}
                            {selectedEntry?.acceleratedDepreciation && (
                                <Row className="mb-4">
                                    <Col md={12}>
                                        <div className="form-group">
                                            <label htmlFor="depreciationPeriodYears" className="form-label fw-medium">
                                                {t("business.assets.depreciation_period_years", "Depreciation Period (Years)")} <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="number"
                                                id="depreciationPeriodYears"
                                                min="1"
                                                className={`form-control ${errors.depreciationPeriodYears ? 'is-invalid' : ''}`}
                                                value={selectedEntry?.depreciationPeriodYears || ''}
                                                onChange={(e) => setSelectedEntry({ ...selectedEntry, depreciationPeriodYears: e.target.value })}
                                                placeholder={t("business.assets.enter_depreciation_period", "Enter depreciation period in years")}
                                            />
                                            {errors.depreciationPeriodYears && <small className="text-danger">{errors.depreciationPeriodYears}</small>}
                                        </div>
                                    </Col>
                                </Row>
                            )}
                            </>)}


                            {/* NFC/RFID Tag UUID - only show if uuid is provided from deep link */}
                            {selectedEntry?.uuid && (
                                <Row className="mb-4">
                                    <Col md={12}>
                                        <div className="form-group">
                                            <label htmlFor="uuid" className="form-label fw-medium">
                                                {t("business.assets.nfc_rfid_tag_id", "NFC/RFID Tag ID")}
                                                <Badge color="info" className="ms-2" style={{ fontSize: '0.65rem' }}>
                                                    {t("business.assets.from_tag", "From Tag")}
                                                </Badge>
                                            </label>
                                            <input
                                                type="text"
                                                id="uuid"
                                                className="form-control"
                                                value={selectedEntry?.uuid || ''}
                                                readOnly
                                                disabled
                                                style={{ backgroundColor: '#f8f9fa', fontFamily: 'monospace' }}
                                            />
                                            <small className="text-muted">{t("business.assets.uuid_prefilled", "This UUID is pre-filled from the scanned NFC/RFID tag")}</small>
                                        </div>
                                    </Col>
                                </Row>
                            )}

                            {apiError && <div className="alert alert-danger">{apiError}</div>}
                    </div>
                    <div className="modal-footer bg-light border-top-0">
                        <button type="button" className="btn btn-outline-secondary" onClick={handleModalClose}>{t("shared.cancel", "Cancel")}</button>
                        <button type="submit" id="assetFormSubmit" className="btn btn-primary" disabled={loading}>
                            {loading ? (
                                <><Spinner size="sm" className="me-2" />{selectedEntry?.id ? t("shared.updating", 'Updating...') : t("shared.creating", 'Creating...')}</>
                            ) : (
                                selectedEntry?.id ? t("business.assets.update_asset", 'Update Asset') : t("business.assets.create_asset", 'Create Asset')
                            )}
                        </button>
                    </div>
                    </form>
                </motion.div>
            </Modal>
            <Modal isOpen={deleteConfirmation.show} toggle={() => !deleteConfirmation.deleting && setDeleteConfirmation(prev => ({...prev, show: false }))} centered>
                <div className="modal-header border-0 pb-0">
                    <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => !deleteConfirmation.deleting && setDeleteConfirmation(prev => ({...prev, show: false }))}
                        aria-label={t("shared.close", "Close")}
                        disabled={deleteConfirmation.deleting}
                    ></button>
                </div>
                <div className="modal-body text-center py-0">
                    <FeatherIcon icon="alert-triangle" size="48" className="text-warning mb-3" />
                    <h5 className="mb-3">{t("business.assets.delete_this_image", "Delete this image?")}</h5>
                    
                    <div className="mb-4 position-relative" style={{ minHeight: '150px' }}>
                        <img 
                            src={deleteConfirmation.imageUrl} 
                            alt="Preview" 
                            className="img-thumbnail rounded"
                            style={{ 
                                maxHeight: '150px',
                                opacity: deleteConfirmation.deleting ? 0.5 : 1
                            }}
                        />
                        {deleteConfirmation.deleting && (
                            <div className="position-absolute top-50 start-50 translate-middle">
                                <div className="spinner-border text-danger" role="status">
                                    <span className="visually-hidden">{t("shared.loading", "Loading...")}</span>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <p className="text-muted mb-4">
                        {t("business.assets.image_permanently_removed", "This image will be permanently removed from this asset.")}
                    </p>
                </div>
                <div className="modal-footer border-0 pt-0 justify-content-center">
                    <button 
                        type="button" 
                        className="btn btn-light" 
                        onClick={() => setDeleteConfirmation(prev => ({...prev, show: false }))}
                        disabled={deleteConfirmation.deleting}
                        style={{ minWidth: '100px' }}
                    >
                        {t("shared.cancel", "Cancel")}
                    </button>
                    <button 
                        type="button" 
                        className="btn btn-danger ms-2" 
                        disabled={deleteConfirmation.deleting}
                        onClick={async () => {
                            setDeleteConfirmation(prev => ({...prev, deleting: true }));
                            
                            const updatedImages = [...currentImages];
                            updatedImages.splice(deleteConfirmation.index, 1);
                            
                            const success = await deleteImageFromAsset(updatedImages);
                            
                            if (success) {
                                setCurrentImages(updatedImages);
                                setSelectedEntry(prev => ({
                                    ...prev,
                                    assetImageUrls: updatedImages
                                }));
                            }
                            
                            setDeleteConfirmation(prev => ({...prev, show: false, deleting: false }));
                        }}
                        style={{ minWidth: '100px' }}
                    >
                        {deleteConfirmation.deleting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                {t("shared.deleting", "Deleting...")}
                            </>
                        ) : (
                            t("shared.delete", 'Delete')
                        )}
                    </button>
                </div>
            </Modal>

            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
                <div className="modal-header">
                    <h5 className="modal-title">{t("shared.confirm_delete", "Confirm Delete")}</h5>
                    <button type="button" className="btn-close" onClick={() => setDeleteModal(false)} aria-label={t("shared.close", "Close")}></button>
                </div>
                <div className="modal-body">
                    <p>{t("shared.are_you_sure_you_want_to_delete_this_ass", "Are you sure you want to delete this asset? This action cannot be undone.")}</p>
                </div>
                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setDeleteModal(false)}>
                        {t("shared.cancel", "Cancel")}
                    </button>
                    <button 
                        type="button" 
                        className="btn btn-danger" 
                        onClick={handleDelete}
                    >
                        {t("shared.delete", "Delete")}
                    </button>
                </div>
            </Modal>

            {/* QR Code Viewer Modal */}
            <Modal isOpen={qrModal} toggle={() => setQrModal(false)} size="md">
                <div className="modal-header">
                    <h5 className="modal-title">{t("shared.qr_code", "QR Code")}</h5>
                    <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setQrModal(false)}
                        aria-label={t("shared.close", "Close")}
                    ></button>
                </div>
                <div className="modal-body text-center">
                    {selectedEntry?.qrImageUrl ? (
                        <div className="card border-0 shadow-sm">
                            <div className="position-relative">
                                <img 
                                    src={selectedEntry.qrImageUrl} 
                                    alt="QR Code" 
                                    className="img-fluid rounded-top"
                                    style={{ 
                                        width: '100%',
                                        maxHeight: '300px',
                                        objectFit: 'contain'
                                    }}
                                />
                                <div className="position-absolute top-0 end-0 p-2">
                                    <button
                                        onClick={() => downloadQRCode(
                                            selectedEntry.qrImageUrl,
                                            selectedEntry.assetName,
                                            selectedEntry.serialNumber
                                        )}
                                        className="btn btn-primary btn-sm rounded-circle"
                                        title={t("business.components.assets.download_qr", "Download QR Code")}
                                        style={{
                                            width: '30px',
                                            height: '30px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <FeatherIcon icon="download" size={14} />
                                    </button>
                                </div>
                            </div>
                            <div className="card-body p-2">
                                <p className="mb-1"><strong>{t("shared.asset", "Asset:")}</strong> {selectedEntry.assetName}</p>
                                <p className="mb-1"><strong>{t("shared.serial_number", "Serial Number")}:</strong> {selectedEntry.serialNumber}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-4">
                            <FeatherIcon icon="alert-circle" size="48" className="text-warning mb-2" />
                            <p className="text-muted">{t("business.assets.no_qr_generated", "No QR code generated for this asset")}</p>
                            <button 
                                type="button" 
                                className="btn btn-success mt-2"
                                onClick={handleQr}
                            >
                                {t("business.assets.generate_qr_code", "Generate QR Code")}
                            </button>
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <button 
                        type="button" 
                        className="btn btn-secondary" 
                        onClick={() => setQrModal(false)}
                    >
                        {t("shared.close", "Close")}
                    </button>
                </div>
            </Modal>

            {/* Attach UUID to Asset Modal */}
            <Modal isOpen={attachUuidModal} toggle={() => setAttachUuidModal(false)} size="lg">
                <div className="modal-header">
                    <h5 className="modal-title">{t("business.assets.attach_tag", "Attach Tag to Asset")}</h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setAttachUuidModal(false)}
                    />
                </div>
                <div className="modal-body">
                    {/* UUID Display */}
                    <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#e8f4fd' }}>
                        <small className="text-muted d-block mb-1">{t("business.assets.attaching_tag_uuid", "Attaching Tag UUID")}</small>
                        <code style={{ fontSize: '0.9rem' }}>{attachUuidData.uuid}</code>
                    </div>

                    {/* Search */}
                    <div className="mb-3">
                        <Input
                            type="text"
                            placeholder={t("business.assets.search_assets_placeholder", "Search assets by name, serial number, or manufacturer...")}
                            value={attachUuidData.searchTerm}
                            onChange={(e) => setAttachUuidData(prev => ({ ...prev, searchTerm: e.target.value }))}
                        />
                    </div>

                    {/* Error */}
                    {attachUuidData.error && (
                        <Alert color="danger" className="mb-3">{attachUuidData.error}</Alert>
                    )}

                    {/* Loading */}
                    {attachUuidData.loading && (
                        <div className="text-center py-4">
                            <Spinner color="primary" />
                            <p className="mt-2 text-muted">{t("shared.loading_assets", "Loading assets...")}</p>
                        </div>
                    )}

                    {/* Assets List */}
                    {!attachUuidData.loading && filteredAssetsWithoutUuid.length === 0 && (
                        <div className="text-center py-4">
                            <FeatherIcon icon="inbox" size={48} className="text-muted mb-2" />
                            <p className="text-muted">{t("business.assets.no_assets_available", "No assets available to attach this tag to.")}</p>
                            <small className="text-muted">{t("business.assets.all_assets_tagged", "All assets already have tags attached.")}</small>
                        </div>
                    )}

                    {!attachUuidData.loading && filteredAssetsWithoutUuid.length > 0 && (
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {filteredAssetsWithoutUuid.map((asset) => (
                                <Card
                                    key={asset.id}
                                    className="mb-2 cursor-pointer hover-shadow"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleSelectAssetForAttachment(asset)}
                                >
                                    <CardBody className="py-3">
                                        <div className="d-flex align-items-center">
                                            <div
                                                className="d-flex align-items-center justify-content-center rounded me-3"
                                                style={{
                                                    width: '48px',
                                                    height: '48px',
                                                    backgroundColor: '#e8f4fd'
                                                }}
                                            >
                                                <FeatherIcon icon="box" size={24} style={{ color: '#0d6efd' }} />
                                            </div>
                                            <div className="flex-grow-1">
                                                <h6 className="mb-1">{asset.assetName}</h6>
                                                <small className="text-muted d-block">
                                                    {t("shared.serial", "Serial:")} {asset.serialNumber}
                                                </small>
                                                <small className="text-muted">
                                                    {asset.manufacturer}
                                                </small>
                                            </div>
                                            <FeatherIcon icon="chevron-right" size={20} className="text-muted" />
                                        </div>
                                    </CardBody>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <Button color="secondary" onClick={() => setAttachUuidModal(false)}>
                        {t("shared.cancel", "Cancel")}
                    </Button>
                </div>
            </Modal>

            {/* Confirmation Modal for Attaching UUID */}
            <Modal isOpen={attachConfirmation.show} toggle={handleCancelAttachConfirmation} centered>
                <div className="modal-header">
                    <h5 className="modal-title">{t("business.assets.confirm_tag_attachment", "Confirm Tag Attachment")}</h5>
                    <button type="button" className="btn-close" onClick={handleCancelAttachConfirmation}></button>
                </div>
                <div className="modal-body">
                    <div className="text-center mb-4">
                        <div
                            className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                            style={{ width: '64px', height: '64px', backgroundColor: '#fff3cd' }}
                        >
                            <FeatherIcon icon="link" size={32} style={{ color: '#856404' }} />
                        </div>
                        <h6>{t("business.assets.confirm_attach_nfc", "Are you sure you want to attach this NFC tag?")}</h6>
                    </div>

                    <div className="bg-light rounded p-3 mb-3">
                        <small className="text-muted d-block mb-1">{t("business.assets.nfc_tag_uuid", "NFC Tag UUID")}</small>
                        <code style={{ fontSize: '0.85rem', color: '#d63384' }}>{attachUuidData.uuid}</code>
                    </div>

                    {attachConfirmation.asset && (
                        <div className="border rounded p-3">
                            <small className="text-muted d-block mb-2">{t("business.assets.will_be_attached_to", "Will be attached to:")}</small>
                            <div className="d-flex align-items-center">
                                <div
                                    className="d-flex align-items-center justify-content-center rounded me-3"
                                    style={{ width: '40px', height: '40px', backgroundColor: '#e8f4fd' }}
                                >
                                    <FeatherIcon icon="box" size={20} style={{ color: '#0d6efd' }} />
                                </div>
                                <div>
                                    <strong>{attachConfirmation.asset.assetName}</strong>
                                    <small className="text-muted d-block">
                                        {t("shared.serial", "Serial:")} {attachConfirmation.asset.serialNumber}
                                    </small>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <Button color="secondary" onClick={handleCancelAttachConfirmation}>
                        {t("shared.cancel", "Cancel")}
                    </Button>
                    <Button color="primary" onClick={handleConfirmAttachUuid} disabled={attachUuidData.loading}>
                        {attachUuidData.loading ? t("shared.attaching", 'Attaching...') : t("business.assets.confirm_attachment", 'Confirm Attachment')}
                    </Button>
                </div>
            </Modal>

        <LocationModal
        isOpen={detailsModal.isOpen}
        toggle={() => setDetailsModal(prev => ({ ...prev, isOpen: false }))}
        title={detailsModal.title}
        mainData={detailsModal.mainData}
        relatedData={detailsModal.relatedData}
        loading={detailsModal.loading}
        formatCreatedDate={formatCreatedDate}
        onTicketClick={handleTicketClick}
        locationData={detailsModal.locationData}
        onLocationUpdated={(updatedLocation) => {
            setDetailsModal(prev => ({
                ...prev,
                locationData: updatedLocation,
                mainData: updatedLocation.locationName,
                title: updatedLocation.locationName
            }));
        }}
      />
            <FileModal
                isOpen={fileModal}
                toggle={handleClose}
                onImport={handleImport}
                onExport={handleExport}
                onDownloadTemplate={handleDownloadTemplate}
                error={apiError || assetImport.error}
                loading={loading}
                importLabel={t("shared.upload_your_file_in_csv_or_xls_format", "Upload your file in CSV or XLS format")}
                exportLabel={t("business.assets.import.export_label", "Export existing assets to Excel")}
                templateLabel={t("shared.use_our_template_to_format_your_file_cor", "Use our template to format your file correctly")}
                importJob={{
                    job: assetImport.job,
                    busy: assetImport.busy,
                    onConfirm: assetImport.commit,
                    onCancel: async () => { await assetImport.cancel(); setFileModal(false); },
                    onClose: () => { assetImport.reset(); setFileModal(false); },
                    fetchAllIssues: assetImport.fetchAllIssues,
                    columnLabels: importColumnLabels,
                }}
            />
            {/* With rows ticked the modal opens seeded with exactly that selection; with none it
                falls back to its own asset picker (the toolbar button's original behaviour). */}
            <AssetTransferModal
                isOpen={showTransferModal}
                toggle={() => setShowTransferModal(false)}
                bulk
                organizationId={organization?.id}
                preselectedAssets={selectedCount > 0 ? preselectedAssets : undefined}
                onSuccess={handleBulkTransferSuccess}
            />
            <AssetModal
            isOpen={reportedByModal.isOpen}
            toggle={() => setReportedByModal(prev => ({ ...prev, isOpen: false, initialActiveTab: null }))}
            userData={reportedByModal.userData}
            loading={reportedByModal.loading}
            initialActiveTab={reportedByModal.initialActiveTab}
            formatCreatedDate={formatCreatedDate}
            onTicketClick={handleTicketClick}
            fetchUserWorkOrders={handleReportedByClick}
            onEditAsset={(asset) => {
                setReportedByModal(prev => ({ ...prev, isOpen: false, initialActiveTab: null }));
                tog_large(asset);
            }}
            onCreateSubAsset={(parentAsset) => {
                setReportedByModal(prev => ({ ...prev, isOpen: false, initialActiveTab: null }));
                tog_large(null, parentAsset);
            }}
            />
             <TicketDetailsModal
            isOpen={ticketModal.isOpen}
            enumData={enumData}
            toggle={() => setTicketModal(prev => ({ ...prev, isOpen: false }))}
            ticketNumber={ticketModal.ticketData?.ticketNumber}
            loading={ticketModal.loading}
            formatCreatedDate={formatCreatedDate}
            onStatusChange={onStatusChange}
            />

            <ConfirmDeleteModal
                isOpen={showDuplicateConfirm}
                toggle={() => setShowDuplicateConfirm(false)}
                onConfirm={() => {
                    setShowDuplicateConfirm(false);
                    setAllowDuplicateAssetId(true);
                    // Clear the validation error state since user confirmed
                    setAssetNumberValidation({
                        isValidating: false,
                        isValid: true,
                        message: '',
                        duplicateAssets: []
                    });
                    // Trigger save again
                    setTimeout(() => {
                        document.getElementById('assetFormSubmit')?.click();
                    }, 100);
                }}
                title={t("business.assets.duplicate_asset_id_detected", "Duplicate Asset ID Detected")}
                confirmText={t("business.assets.yes_create_anyway", "Yes, Create Anyway")}
                cancelText={t("shared.cancel", "Cancel")}
                confirmColor="warning"
            >
                {/* Custom content using children prop */}
                <div className="text-center">
                    <FeatherIcon icon="alert-triangle" size="48" className="text-warning mb-3" />
                    <p className="mb-3">
                        {t("business.assets.duplicate_asset_message", 'Asset ID "{{assetNumber}}" already exists ({{duplicateCount}} assets).', { assetNumber: selectedEntry?.assetNumber, duplicateCount: assetNumberValidation.duplicateCount })}
                    </p>

                    {assetNumberValidation.duplicateAssets && assetNumberValidation.duplicateAssets.length > 0 && (
                        <div>
                            <p className="mb-2">
                                <strong>{t("business.assets.existing_assets_with_id", "Existing assets with this ID")}</strong>
                                {' '}({t("business.assets.showing_x_of_y", "showing {{x}} of {{y}}", { x: Math.min(10, assetNumberValidation.duplicateAssets.length), y: assetNumberValidation.duplicateCount })}):
                            </p>
                            <div className="mb-3">
                                {assetNumberValidation.duplicateAssets.slice(0, 10).map((asset, index) => (
                                    <div key={asset.id || index}>
                                        • {asset.assetName || t("shared.n_a", 'N/A')} - {asset.serialNumber || t("shared.n_a", 'N/A')}
                                    </div>
                                ))}
                            </div>
                            {assetNumberValidation.duplicateCount > 10 && (
                                <p className="text-muted">
                                    +{assetNumberValidation.duplicateCount - 10} {t("shared.more", "more...")}
                                </p>
                            )}
                        </div>
                    )}

                    <p className="mt-3">{t("business.assets.create_duplicate_confirm", "Are you sure you want to create a duplicate Asset ID?")}</p>
                </div>
            </ConfirmDeleteModal>
        </div>
    );
};

export default Assets;